import { useEffect, useMemo, useRef, useState } from 'react';
import { Save, User, Bell, Shield, Globe, Database, Mail, Smartphone, Plug, Loader2, AlertCircle, CheckCircle2, TrendingUp, Plus, Trash2, Edit, DollarSign, Palette, ExternalLink, FileText, ImageUp, GripVertical, X, Timer, RefreshCw } from 'lucide-react';
import { settingsService } from '../services/settingsService';
import { webAppVersionService, type WebAppVersionInfo } from '../services/webAppVersionService';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { useAppAlert } from '../components/AppAlertProvider';
import { platformConfig } from '../config/platform';
import { getStoredThemePreference, getUserThemePreferenceSettingKey, setStoredThemePreference, type ThemePreference } from '../utils/themePreference';
import { customerService } from '../services/customerService';
import { staticDefinitionService } from '../services/staticDefinitionService';
import { procedureStepService } from '../services/procedureStepService';
import { bidService } from '../services/bidService';

const PROFILE_SETTINGS_KEY = 'settings_company_profile';
const NOTIFICATION_SETTINGS_KEY = 'settings_notification_preferences';
const SYSTEM_SETTINGS_KEY = 'settings_system_preferences';
const INTEGRATION_SETTINGS_KEY = 'settings_integration_preferences';
const HARCIRAH_SETTINGS_KEY = 'settings_harcirah_preferences';

const defaultProfileData = {
  companyName: 'Doğuş Çay A.Ş.',
  contactName: 'Operasyon Yöneticisi',
  email: 'operasyon@doguscay.com.tr',
  phone: '0212 123 4567',
  address: 'İstanbul, Türkiye',
  taxNumber: '1234567890',
  googlePlayUrl: '',
  appStoreUrl: '',
};

const defaultOptimizationSettings = {
  ozmalKmCost: '12',
  spotKmCost: '18',
  averageSpeedKmH: '75',
  decisionDriverMonthlyFixedCost: '105000',
  decisionDailyPersonnelCost: '3500',
  decisionFuelKmCost: '14',
  decisionAnalysisDayCount: '18',
  decisionMonthDayCount: '31',
  decisionProjectionFactor: '1.7222',
  decisionBackhaulFuelFactor: '1.2',
  decisionMaxTriangleEmptyKm: '200',
  decisionOsmangaziBridgeCost: '2505',
  decisionYssBridgeCost: '720',
  decisionShortDistancePrime: '350',
  decisionWaitingCost50Km: '750',
  decisionNightCost: '1500',
  ozmalCostPeriod: '',
  ozmalCostPeriodStart: '',
  ozmalCostPeriodEnd: '',
  ozmalAnnualKm: '120000',
  ozmalTrafficInsurance: '60000',
  ozmalCasco: '180000',
  ozmalMtv: '32000',
  ozmalInspection: '6017',
  ozmalFuelLiters: '36000',
  ozmalFuelUnitPrice: '60',
  ozmalTireCount: '3',
  ozmalTireUnitPrice: '16000',
  ozmalDriverCount: '1',
  ozmalDriverGross: '122000',
  ozmalMaintenanceTotal: '150000',
};

const NO_SPINNER = "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";
const toNumber = (value: string | number | undefined) => Number(String(value || '0').replace(',', '.')) || 0;
const toFixed4 = (value: number) => value.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const toFixed2 = (value: number) => value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const defaultNotificationSettings = {
  emailNotifications: true,
  smsNotifications: false,
  newShipment: true,
  shipmentDelivered: true,
  delayAlert: true,
  documentUploaded: false,
  maintenanceReminder: true,
  supportNotificationRoleIds: ['admin'],
};

const defaultSystemSettings = {
  language: 'tr',
  timezone: 'Europe/Istanbul',
  dateFormat: 'DD/MM/YYYY',
  currency: 'TRY',
  autoBackup: true,
  dataRetention: '365',
  minimumFreightCost: '0',
  basePriceRestrictionEnabled: false,
  paymentTermDays: '30',
  maxBidOverBasePricePercent: '15',
  defaultShipmentContactPhone: '',
  archiveNextFolderNumber: '1',
  signedPdfEmailBody: 'Secilen sevkiyatlarin imzali on onay PDF belgeleri ekte yer almaktadir.',
  locationReminderMessage: 'Merhaba, uygulamadan konum paylaşımınızı kontrol edip tekrar konum göndermenizi rica ederiz.',
  autoCalculateStopFees: true,
  unitStopFee: '0',
  extraStopFee: '0',
  customerToDepotBasePriceDiscountPercent: '20',
  demurrageEnabled: true,
  demurrageTransitionDate: '',
  demurrageDailyFee: '0',
  demurrageGeofenceKm: '2',
  demurrageExitGeofenceMultiplier: '1',
  demurrageMaxLookaheadDays: '2',
  demurrageCustomerCutoffTime: '13:00',
  demurrageDepotCutoffTime: '13:00',
  internalFreightInvoiceCustomerId: '',
  internalFreightInvoiceCustomerName: '',
  spotLiveTrackingRecencyMinutes: '120',
  driverAutoControlEnabled: false,
  driverAutoControlSpeedLimit: '100',
  driverAutoControlIdleMinutes: '60',
  driverAutoControlPush: true,
  driverAutoControlWhatsapp: false,
  driverAutoControlMessage: 'Sayın [Sürücü Adı], [Plaka] numaralı aracınızla [İhlal] tespit edilmiştir. Lütfen hız ve yakıt limitlerine dikkat ediniz. Güvenli sürüşler.',
  missingDriverCodeViolationEnabled: true,
  missingDriverCodeWarehouseRadiusKm: '2',
  missingDriverCodeMovementKm: '1',
  driverViolationHrTo: '',
  driverViolationHrCc: '',
  driverViolationDefenseOtpEnabled: true,
  driverViolationDefenseRequestsEnabled: true,
  driverViolationDefenseNotificationsEnabled: true,
  driverViolationSummaryEnabled: false,
  driverViolationSummaryTime: '09:00',
  driverViolationSummaryTo: '',
  driverViolationSummaryCc: '',
  offerNotificationRadiusKm: '0',
  rampOperationDurationMinutes: '60',
  weighbridgeProximityRadiusKm: '1',
  timelineArrivalRadiusKm: '0.5',
  timelineDepartureRadiusKm: '0.7',
  googleMapsApiKey: '',
  bingMapsApiKey: '',
  openRouteServiceApiKey: '',
  requiredSpotDriverDocuments: [] as string[],
  allowedSpotDriverIds: [] as string[],
  twoFactorAuthEnabled: false,
  smsRequestProtection: false,
  bulkSmsDailyLimit: '2000',
  smsNotificationsEnabled: false,
  };

const defaultHarcirahSettings = {
  prim: '3.56',
  maas: '45000',
  kisaMesafePrim: '350',
  beklemeBedeli: '750',
  vergi: '1.63',
  dorseKesme: '150',
  araDepoSevki: '150',
  hamaliye: '100',
  aylikMaksimumHarcirah: '23400',
  gunlukAgi: '900',
};

const SPOT_DRIVER_REQUIRED_DOCUMENT_OPTIONS = [
  { value: 'tax-plate', label: 'Vergi Levhası' },
  { value: 'license', label: 'Ehliyet' },
  { value: 'tractor-license', label: 'Çekici Ruhsat' },
  { value: 'trailer-license', label: 'Dorse Ruhsat' },
  { value: 'src', label: 'SRC Belgesi' },
  { value: 'vehicle-insurance', label: 'Araç Sigortası' },
  { value: 'contract', label: 'Taşıma Sözleşmesi' },
  { value: 'carrier-authority', label: 'Taşıyıcı Yetki Belgesi' },
  { value: 'psychotechnic', label: 'Psikoteknik Belgesi' },
];

const defaultIntegrationSettings = {
  tachographEnabled: false,
  tachographApiUrl: '',
  tachographApiKey: '',
  tachographTimeout: '30',
  tachographAutoSync: true,
  tachographSyncInterval: '60',
  vehicleTrackingEnabled: false,
  vehicleTrackingApiUrl: '',
  vehicleTrackingApiKey: '',
  vehicleTrackingTimeout: '30',
  vehicleTrackingAutoSync: true,
  vehicleTrackingSyncInterval: '30',
  vehicleTrackingProvider: 'generic',
  stokbarCustomerSyncEnabled: false,
  stokbarCustomerLastSyncAt: '',
};

export function Settings() {
  const { user, logout } = useAuth();
  const { showAlert } = useAppAlert();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState(defaultProfileData);
  const [notificationSettings, setNotificationSettings] = useState(defaultNotificationSettings);
  const [systemSettings, setSystemSettings] = useState(defaultSystemSettings);
  const [integrationSettings, setIntegrationSettings] = useState(defaultIntegrationSettings);
  const [harcirahSettings, setHarcirahSettings] = useState(defaultHarcirahSettings);
  const [optimizationSettings, setOptimizationSettings] = useState(defaultOptimizationSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingState, setSavingState] = useState<Record<string, boolean>>({});
  const [securityForm, setSecurityForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [calculatingSpotCost, setCalculatingSpotCost] = useState(false);
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => getStoredThemePreference(user?.id));
  const [roles, setRoles] = useState<Array<{ id: string; name: string }>>([]);
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [staticDefinitions, setStaticDefinitions] = useState<any[]>([]);
  const [staticDefinitionModalOpen, setStaticDefinitionModalOpen] = useState(false);
  const [editingStaticDefinition, setEditingStaticDefinition] = useState<any | null>(null);
  const [staticDefinitionForm, setStaticDefinitionForm] = useState({ key: '', name: '', inputType: 'select', description: '' });
  const [itemManagerDefinition, setItemManagerDefinition] = useState<any | null>(null);
  const [itemManagerRows, setItemManagerRows] = useState<any[]>([]);
  const [newStaticItemValue, setNewStaticItemValue] = useState('');
  const [procedureSteps, setProcedureSteps] = useState<any[]>([]);
  const [externalDrivers, setExternalDrivers] = useState<Array<{ id: string; full_name: string; phone: string; email: string }>>([]);
  const [deployedVersionInfo, setDeployedVersionInfo] = useState<WebAppVersionInfo | null>(null);
  const [webVersionChecking, setWebVersionChecking] = useState(false);
  const [spotDriverSearch, setSpotDriverSearch] = useState('');
  const [spotDriverDropdownOpen, setSpotDriverDropdownOpen] = useState(false);
  const spotDriverDropdownRef = useRef<HTMLDivElement>(null);
  const [procedureStepModalOpen, setProcedureStepModalOpen] = useState(false);
  const [editingProcedureStep, setEditingProcedureStep] = useState<any | null>(null);
  const [procedureStepForm, setProcedureStepForm] = useState({ step_order: 0, title: '', description: '', category: 'ozmal-surec', image_url: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const showGooglePlaySettings = platformConfig.os !== 'ios';
  const showAppStoreSettings = platformConfig.os !== 'android';

  const OPTIMIZATION_FIELDS = Object.keys(defaultOptimizationSettings);
  const optimizationKeys = OPTIMIZATION_FIELDS.map((f) => `optimization_${f}`);

  const selectedSpotDrivers = useMemo(() => {
    const ids = new Set(systemSettings.allowedSpotDriverIds || []);
    return externalDrivers.filter((d) => ids.has(d.id));
  }, [systemSettings.allowedSpotDriverIds, externalDrivers]);

  const filteredExternalDrivers = useMemo(() => {
    const q = spotDriverSearch.trim().toLocaleLowerCase('tr-TR');
    const selectedIds = new Set(systemSettings.allowedSpotDriverIds || []);
    const pool = externalDrivers;
    if (!q) return pool.filter((d) => !selectedIds.has(d.id));
    return pool.filter((d) => {
      const haystack = `${d.full_name || ''} ${d.phone || ''} ${d.email || ''}`.toLocaleLowerCase('tr-TR');
      return haystack.includes(q);
    });
  }, [externalDrivers, spotDriverSearch, systemSettings.allowedSpotDriverIds]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (spotDriverDropdownRef.current && !spotDriverDropdownRef.current.contains(e.target as Node)) {
        setSpotDriverDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        setError('');
        const [profile, notifications, system, integrations, harcirah, allSettings, customersResponse, staticDefinitionsResponse] = await Promise.all([
          settingsService.getSettingValue(PROFILE_SETTINGS_KEY, defaultProfileData),
          settingsService.getSettingValue(NOTIFICATION_SETTINGS_KEY, defaultNotificationSettings),
          settingsService.getSettingValue(SYSTEM_SETTINGS_KEY, defaultSystemSettings),
          settingsService.getSettingValue(INTEGRATION_SETTINGS_KEY, defaultIntegrationSettings),
          settingsService.getSettingValue(HARCIRAH_SETTINGS_KEY, defaultHarcirahSettings),
          settingsService.getSettings(),
          customerService.getCustomers({ status: 'active' }).catch(() => ({ data: [] })),
          staticDefinitionService.getAll().catch(() => ({ data: [] })),
        ]);
        const rolesResponse: any = await userService.getRoles().catch(() => ({ data: [] }));

        const optimizationMap: Record<string, string> = {};
        for (const row of allSettings.data || []) {
          if (row.setting_key.startsWith('optimization_')) {
            const field = row.setting_key.replace('optimization_', '');
            optimizationMap[field] = row.setting_value;
          }
        }
        const mergedOptimization = { ...defaultOptimizationSettings, ...optimizationMap };

        setProfileData({ ...defaultProfileData, ...profile });
        setNotificationSettings({ ...defaultNotificationSettings, ...notifications });
        setSystemSettings({ ...defaultSystemSettings, ...system });
        setIntegrationSettings({ ...defaultIntegrationSettings, ...integrations });
        setHarcirahSettings({ ...defaultHarcirahSettings, ...harcirah });
        setOptimizationSettings(mergedOptimization);
        setRoles(Array.isArray(rolesResponse?.data) ? rolesResponse.data.map((item: any) => ({ id: String(item.id), name: item.name || item.id })) : []);
        setCustomers(Array.isArray((customersResponse as any)?.data)
          ? (customersResponse as any).data.map((item: any) => ({ id: String(item.id), name: item.company_name || item.name || `Müşteri #${item.id}` }))
          : []);
        setStaticDefinitions(Array.isArray((staticDefinitionsResponse as any)?.data) ? (staticDefinitionsResponse as any).data : []);
        const stepsResponse = await procedureStepService.getAll().catch(() => []);
        setProcedureSteps(Array.isArray(stepsResponse) ? stepsResponse : []);

        try {
          let externalDriverRows: any[] = [];
          try {
            const extDriversResponse: any = await bidService.getExternalDrivers();
            if (extDriversResponse?.success && Array.isArray(extDriversResponse.data)) {
              externalDriverRows = extDriversResponse.data;
            }
          } catch (error: any) {
            if (error?.status !== 404) throw error;
            const fallbackResponse: any = await userService.getUsers({ role: 'external-driver', status: 'active' });
            externalDriverRows = Array.isArray(fallbackResponse?.data) ? fallbackResponse.data : [];
          }

          if (externalDriverRows.length > 0) {
            setExternalDrivers(externalDriverRows.map((d: any) => ({
              id: String(d.id),
              full_name: d.full_name || d.fullName || d.name || d.phone || d.email || `Sürücü #${d.id}`,
              phone: d.phone || '',
              email: d.email || '',
            })));
          } else {
            setExternalDrivers([]);
          }
        } catch {
          setExternalDrivers([]);
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
        setError('Ayarlar yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  useEffect(() => {
    const syncTheme = async () => {
      if (!user?.id) {
        setThemePreference(getStoredThemePreference());
        return;
      }
      const fallbackTheme = getStoredThemePreference(user.id);
      try {
        const storedTheme = await settingsService.getMySettingValue<ThemePreference>(getUserThemePreferenceSettingKey(user.id), fallbackTheme);
        const nextTheme = storedTheme === 'light' ? 'light' : 'dark';
        setThemePreference(nextTheme);
        setStoredThemePreference(nextTheme, user.id);
      } catch {
        setThemePreference(fallbackTheme);
      }
    };

    void syncTheme();
  }, [user?.id]);

  const saveSection = async (section: string, key: string, value: unknown, category: string, description: string) => {
    try {
      setSavingState((current) => ({ ...current, [section]: true }));
      setError('');
      setSuccess('');
      await settingsService.saveSetting(key, value, {
        category,
        description,
        updatedBy: user?.id,
      });
      setSuccess('Ayarlar başarıyla kaydedildi');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(`Failed to save ${section} settings:`, err);
      setError('Ayarlar kaydedilirken bir hata oluştu');
    } finally {
      setSavingState((current) => ({ ...current, [section]: false }));
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveSection('profile', PROFILE_SETTINGS_KEY, profileData, 'profile', 'Şirket profil ayarları');
  };

  const handleSaveNotifications = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveSection('notifications', NOTIFICATION_SETTINGS_KEY, notificationSettings, 'notification', 'Bildirim tercihleri');
  };

  const handleSaveSystem = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSavingState((current) => ({ ...current, system: true }));
      setError('');
      setSuccess('');
      const res = await settingsService.saveSetting(SYSTEM_SETTINGS_KEY, systemSettings, {
        category: 'system',
        description: 'Sistem tercihleri',
        updatedBy: user?.id,
      });
      const forceLogout = Boolean((res as any)?.data?.forceLogout) || Boolean((res as any)?.forceLogout);
      if (forceLogout) {
        setSuccess('Çift doğrulama aktifleştirildi. Tüm oturumlar sonlandırıldı, tekrar giriş yapmanız gerekiyor.');
        setTimeout(() => {
          logout();
          const redirectKey = 'redirectAfterLogin';
          sessionStorage.removeItem(redirectKey);
          window.location.href = '/login';
        }, 1500);
      } else {
        setSuccess('Ayarlar başarıyla kaydedildi');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Failed to save system settings:', err);
      setError('Ayarlar kaydedilirken bir hata oluştu');
    } finally {
      setSavingState((current) => ({ ...current, system: false }));
    }
  };

  const checkDeployedVersion = async () => {
    setWebVersionChecking(true);
    try {
      const info = await webAppVersionService.fetchDeployedVersion();
      setDeployedVersionInfo(info);
      if (info && info.buildId !== webAppVersionService.getCurrentBuildId()) {
        setSuccess('Yayında daha yeni bir sürüm mevcut');
      } else {
        setSuccess('Uygulama güncel');
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to check deployed version:', err);
      setError('Sürüm bilgisi alınamadı');
    } finally {
      setWebVersionChecking(false);
    }
  };

  const handleSaveThemePreference = async () => {
    const nextTheme = themePreference === 'light' ? 'light' : 'dark';
    setStoredThemePreference(nextTheme, user?.id);
    setThemePreference(nextTheme);

    if (!user?.id) {
      setSuccess('Tema tercihi kaydedildi');
      setTimeout(() => setSuccess(''), 3000);
      return;
    }

    try {
      setSavingState((current) => ({ ...current, theme: true }));
      await settingsService.saveMySetting(getUserThemePreferenceSettingKey(user.id), nextTheme, {
        category: 'user',
        description: 'Kullanıcı tema tercihi',
        type: 'string',
      });
      setSuccess('Tema tercihi kaydedildi');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save theme preference:', err);
      setError('Tema tercihi kaydedilirken bir hata oluştu');
    } finally {
      setSavingState((current) => ({ ...current, theme: false }));
    }
  };

  const handleSaveIntegrations = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveSection('integrations', INTEGRATION_SETTINGS_KEY, integrationSettings, 'integration', 'Entegrasyon tercihleri');
  };

  const calculateSpotCostFromHistory = async () => {
    setCalculatingSpotCost(true);
    try {
      const { apiService } = await import('../services/apiService');
      const response = await apiService.post('/settings/calculate-spot-cost');
      const data = response;
      if (data.success && data.data?.averageCost) {
        setOptimizationSettings(prev => ({ ...prev, spotKmCost: data.data.averageCost.toFixed(2) }));
        showAlert({ tone: 'success', title: 'Hesaplandı', message: `Ortalama spot km maliyeti: ${data.data.averageCost.toFixed(2)} TL/km (${data.data.recordCount} kayıt baz alındı)` });
      } else {
        showAlert({ tone: 'error', title: 'Hata', message: data.message || 'Hesaplama başarısız' });
      }
    } catch (err) {
      showAlert({ tone: 'error', title: 'Hata', message: 'Sunucu hatası' });
    } finally {
      setCalculatingSpotCost(false);
    }
  };

  const handleSaveOptimization = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSavingState((current) => ({ ...current, optimization: true }));
      setError('');
      setSuccess('');
      const toSave = Object.entries({
        ...optimizationSettings,
        ozmalKmCost: computedOzmalKmCost.toFixed(2),
        decisionDailyPersonnelCost: computedDecisionDailyPersonnelCost.toFixed(2),
        decisionProjectionFactor: computedDecisionProjectionFactor.toFixed(4),
      }).map(([key, value]) => ({
        key: `optimization_${key}`,
        value,
      }));
      await settingsService.updateSettings(toSave, user?.id);
      setSuccess('Ayarlar başarıyla kaydedildi');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save optimization settings:', err);
      setError('Ayarlar kaydedilirken bir hata oluştu');
    } finally {
      setSavingState((current) => ({ ...current, optimization: false }));
    }
  };

  const handleSaveHarcirah = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingState((current) => ({ ...current, harcirah: true }));
    setError('');
    setSuccess('');
    try {
      await settingsService.setSettingValue(HARCIRAH_SETTINGS_KEY, harcirahSettings, user?.id);
      setSuccess('Harcırah ayarları kaydedildi');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save harcirah settings:', err);
      setError('Harcırah ayarları kaydedilirken hata oluştu');
    } finally {
      setSavingState((current) => ({ ...current, harcirah: false }));
    }
  };

  const openStaticDefinitionModal = (definition?: any) => {
    setEditingStaticDefinition(definition || null);
    setStaticDefinitionForm({
      key: definition?.key || '',
      name: definition?.name || '',
      inputType: definition?.inputType || 'select',
      description: definition?.description || '',
    });
    setStaticDefinitionModalOpen(true);
  };

  const closeStaticDefinitionModal = () => {
    setStaticDefinitionModalOpen(false);
    setEditingStaticDefinition(null);
    setStaticDefinitionForm({ key: '', name: '', inputType: 'select', description: '' });
  };

  const refreshStaticDefinitions = async () => {
    const response: any = await staticDefinitionService.getAll();
    setStaticDefinitions(Array.isArray(response?.data) ? response.data : []);
  };

  const handleSaveStaticDefinition = async () => {
    try {
      if (!staticDefinitionForm.key.trim() || !staticDefinitionForm.name.trim()) {
        setError('Statik tanım ve tanım türü zorunludur');
        return;
      }
      if (editingStaticDefinition) {
        await staticDefinitionService.updateDefinition(editingStaticDefinition.id, staticDefinitionForm);
      } else {
        await staticDefinitionService.createDefinition(staticDefinitionForm);
      }
      await refreshStaticDefinitions();
      closeStaticDefinitionModal();
      setSuccess('Statik tanım kaydedildi');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save static definition:', err);
      setError('Statik tanım kaydedilirken bir hata oluştu');
    }
  };

  const handleDeleteStaticDefinition = async (definition: any) => {
    if (!window.confirm(`${definition.name} tanımını silmek istediğinize emin misiniz?`)) return;
    try {
      await staticDefinitionService.deleteDefinition(definition.id);
      await refreshStaticDefinitions();
    } catch (err) {
      console.error('Failed to delete static definition:', err);
      setError('Statik tanım silinirken bir hata oluştu');
    }
  };

  const openItemManager = (definition: any) => {
    setItemManagerDefinition(definition);
    setItemManagerRows(Array.isArray(definition?.items) ? definition.items.map((item: any) => ({ ...item })) : []);
    setNewStaticItemValue('');
  };

  const isVehicleDocumentDefinition = (definition: any) => ['tractor_trailer_document_types', 'vehicle_document_types'].includes(String(definition?.key || '').trim());

  const setVehicleDocumentScopeChecked = (itemId: string | number, scope: 'tractor' | 'trailer', checked: boolean) => {
    setItemManagerRows((current) => current.map((row) => {
      if (row.id !== itemId) return row;
      const currentScope = Array.isArray(row.vehicleDocumentScope) && row.vehicleDocumentScope.length > 0 ? row.vehicleDocumentScope : ['tractor', 'trailer'];
      const nextScope = checked ? Array.from(new Set([...currentScope, scope])) : currentScope.filter((value: string) => value !== scope);
      return { ...row, vehicleDocumentScope: nextScope };
    }));
  };

  const handleCreateStaticItem = async () => {
    if (!itemManagerDefinition || !newStaticItemValue.trim()) return;
    try {
      await staticDefinitionService.createItem(itemManagerDefinition.id, {
        value: newStaticItemValue.trim(),
        order: itemManagerRows.length + 1,
        assignmentPriority: 0,
        vehicleDocumentScope: isVehicleDocumentDefinition(itemManagerDefinition) ? ['tractor', 'trailer'] : [],
        isActive: true,
      });
      const response: any = await staticDefinitionService.getByKey(itemManagerDefinition.key);
      const nextDefinition = response?.data;
      setItemManagerDefinition(nextDefinition);
      setItemManagerRows(Array.isArray(nextDefinition?.items) ? nextDefinition.items : []);
      await refreshStaticDefinitions();
      setNewStaticItemValue('');
    } catch (err) {
      console.error('Failed to create static definition item:', err);
      setError('Statik tanım değeri eklenemedi');
    }
  };

  const handleUpdateStaticItem = async (item: any) => {
    try {
      await staticDefinitionService.updateItem(item.id, {
        value: item.value,
        order: item.order,
        assignmentPriority: item.assignmentPriority,
        vehicleDocumentScope: Array.isArray(item.vehicleDocumentScope) ? item.vehicleDocumentScope : [],
        isActive: item.isActive,
      });
      await refreshStaticDefinitions();
    } catch (err) {
      console.error('Failed to update static definition item:', err);
      setError('Statik tanım değeri güncellenemedi');
    }
  };

  const handleDeleteStaticItem = async (item: any) => {
    try {
      await staticDefinitionService.deleteItem(item.id);
      const nextRows = itemManagerRows.filter((row) => row.id !== item.id);
      setItemManagerRows(nextRows);
      await refreshStaticDefinitions();
    } catch (err) {
      console.error('Failed to delete static definition item:', err);
      setError('Statik tanım değeri silinemedi');
    }
  };

  const openProcedureStepModal = (step?: any) => {
    if (step) {
      setEditingProcedureStep(step);
      setProcedureStepForm({ step_order: step.step_order, title: step.title, description: step.description || '', category: step.category || 'ozmal-surec', image_url: step.image_url || '' });
    } else {
      setEditingProcedureStep(null);
      setProcedureStepForm({ step_order: procedureSteps.length + 1, title: '', description: '', category: 'ozmal-surec', image_url: '' });
    }
    setProcedureStepModalOpen(true);
  };

  const handleSaveProcedureStep = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSavingState((current) => ({ ...current, procedureSteps: true }));
      if (editingProcedureStep) {
        await procedureStepService.update(editingProcedureStep.id, procedureStepForm);
      } else {
        await procedureStepService.create(procedureStepForm);
      }
      const steps = await procedureStepService.getAll();
      setProcedureSteps(Array.isArray(steps) ? steps : []);
      setProcedureStepModalOpen(false);
      setEditingProcedureStep(null);
      setSuccess(editingProcedureStep ? 'Adım güncellendi' : 'Adım oluşturuldu');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Save procedure step error:', err);
      setError('Adım kaydedilirken hata');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSavingState((current) => ({ ...current, procedureSteps: false }));
    }
  };

  const handleDeleteProcedureStep = async (step: any) => {
    if (!window.confirm(`"${step.title}" adımını silmek istediğinize emin misiniz?`)) return;
    try {
      await procedureStepService.delete(step.id);
      const steps = await procedureStepService.getAll();
      setProcedureSteps(Array.isArray(steps) ? steps : []);
      setSuccess('Adım silindi');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Delete procedure step error:', err);
      setError('Adım silinemedi');
    }
  };

  const handleUploadProcedureImage = async (stepId: number, file: File) => {
    try {
      setUploadingImage(true);
      const result = await procedureStepService.uploadImage(stepId, file);
      const steps = await procedureStepService.getAll();
      setProcedureSteps(Array.isArray(steps) ? steps : []);
      setProcedureStepForm((prev) => ({ ...prev, image_url: result.image_url }));
      setSuccess('Resim yüklendi');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Upload image error:', err);
      setError('Resim yüklenemedi');
    } finally {
      setUploadingImage(false);
    }
  };

  const annualKm = toNumber(optimizationSettings.ozmalAnnualKm);
  const fixedAnnualTotal = toNumber(optimizationSettings.ozmalTrafficInsurance)
    + toNumber(optimizationSettings.ozmalCasco)
    + toNumber(optimizationSettings.ozmalMtv)
    + toNumber(optimizationSettings.ozmalInspection);
  const fixedKmCost = annualKm > 0 ? fixedAnnualTotal / annualKm : 0;

  const fuelAnnualTotal = toNumber(optimizationSettings.ozmalFuelLiters) * toNumber(optimizationSettings.ozmalFuelUnitPrice);
  const fuelKmCost = annualKm > 0 ? fuelAnnualTotal / annualKm : 0;

  const tireAnnualTotal = toNumber(optimizationSettings.ozmalTireCount) * toNumber(optimizationSettings.ozmalTireUnitPrice);
  const tireKmCost = annualKm > 0 ? tireAnnualTotal / annualKm : 0;

  const driverAnnualTotal = toNumber(optimizationSettings.ozmalDriverCount) * toNumber(optimizationSettings.ozmalDriverGross) * 12;
  const driverKmCost = annualKm > 0 ? driverAnnualTotal / annualKm : 0;

  const maintenanceAnnualTotal = toNumber(optimizationSettings.ozmalMaintenanceTotal);
  const maintenanceKmCost = annualKm > 0 ? maintenanceAnnualTotal / annualKm : 0;

  const totalAnnualCost = fixedAnnualTotal + fuelAnnualTotal + tireAnnualTotal + driverAnnualTotal + maintenanceAnnualTotal;
  const computedOzmalKmCost = annualKm > 0 ? totalAnnualCost / annualKm : 0;
  const computedDecisionDailyPersonnelCost = toNumber(optimizationSettings.decisionMonthDayCount) > 0
    ? toNumber(optimizationSettings.decisionDriverMonthlyFixedCost) / toNumber(optimizationSettings.decisionMonthDayCount)
    : 0;
  const computedDecisionProjectionFactor = toNumber(optimizationSettings.decisionAnalysisDayCount) > 0
    ? toNumber(optimizationSettings.decisionMonthDayCount) / toNumber(optimizationSettings.decisionAnalysisDayCount)
    : 0;

  const handleChangePassword = async () => {
    if (!user?.id) return;
    if (!securityForm.currentPassword || !securityForm.newPassword || !securityForm.confirmPassword) {
      showAlert({ tone: 'warning', title: 'Eksik Bilgi', message: 'Lütfen tüm şifre alanlarını doldurun.' });
      return;
    }
    if (securityForm.newPassword !== securityForm.confirmPassword) {
      showAlert({ tone: 'warning', title: 'Şifreler Uyuşmuyor', message: 'Yeni şifre ve tekrar şifre alanları aynı olmalıdır.' });
      return;
    }
    if (securityForm.newPassword.length < 8) {
      showAlert({ tone: 'warning', title: 'Şifre Yetersiz', message: 'Yeni şifre en az 8 karakter olmalıdır.' });
      return;
    }

    try {
      setSavingState((current) => ({ ...current, security: true }));
      await userService.changePassword(user.id, {
        currentPassword: securityForm.currentPassword,
        newPassword: securityForm.newPassword,
      });
      setSecurityForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showAlert({ tone: 'success', title: 'Şifre Güncellendi', message: 'Şifreniz başarıyla güncellendi.' });
    } catch (error: any) {
      console.error('Password change failed:', error);
      showAlert({ tone: 'error', title: 'Şifre Güncellenemedi', message: error?.message || 'Şifre değiştirilirken hata oluştu.' });
    } finally {
      setSavingState((current) => ({ ...current, security: false }));
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress.trim()) return;
    try {
      setSavingState((current) => ({ ...current, testEmail: true }));
      await settingsService.sendTestEmail(testEmailAddress.trim());
      showAlert({ tone: 'success', title: 'Test E-Postası Gönderildi', message: `${testEmailAddress.trim()} adresine test e-postası gönderildi.` });
    } catch (error: any) {
      console.error('Send test email failed:', error);
      showAlert({ tone: 'error', title: 'Gönderilemedi', message: error?.message || 'Test e-postası gönderilirken hata oluştu.' });
    } finally {
      setSavingState((current) => ({ ...current, testEmail: false }));
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profil', icon: User },
    { id: 'notifications', label: 'Bildirimler', icon: Bell },
    { id: 'security', label: 'Güvenlik', icon: Shield },
    { id: 'integrations', label: 'Entegrasyonlar', icon: Plug },
    { id: 'optimization', label: 'Optimizasyon', icon: TrendingUp },
    { id: 'harcirah', label: 'Harcırah', icon: DollarSign },
    { id: 'static-definitions', label: 'Statik Tanımlar', icon: Database },
    { id: 'procedure-steps', label: 'Prosedür Adımları', icon: FileText },
    { id: 'system', label: 'Sistem', icon: Database },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Ayarlar</h1>
        <p className="text-gray-500 mt-1">Sistem ve hesap ayarlarını yönetin</p>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-lg shadow p-10 flex items-center justify-center gap-3 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <span>Ayarlar yükleniyor...</span>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <nav className="space-y-1 p-4">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <User className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">Şirket Bilgileri</h2>
                </div>
                <form onSubmit={handleSaveProfile} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Şirket Adı *
                      </label>
                      <input
                        type="text"
                        required
                        value={profileData.companyName}
                        onChange={(e) => setProfileData({ ...profileData, companyName: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Yetkili Kişi *
                      </label>
                      <input
                        type="text"
                        required
                        value={profileData.contactName}
                        onChange={(e) => setProfileData({ ...profileData, contactName: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        E-posta *
                      </label>
                      <input
                        type="email"
                        required
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        required
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vergi Numarası
                      </label>
                      <input
                        type="text"
                        value={profileData.taxNumber}
                        onChange={(e) => setProfileData({ ...profileData, taxNumber: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Adres
                      </label>
                      <textarea
                        value={profileData.address}
                        onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                      />
                    </div>
                  </div>

                  <div className={`grid grid-cols-1 ${showGooglePlaySettings && showAppStoreSettings ? 'md:grid-cols-2' : ''} gap-4`}>
                    {showGooglePlaySettings && <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Google Play URL
                      </label>
                      <input
                        type="url"
                        value={profileData.googlePlayUrl}
                        onChange={(e) => setProfileData({ ...profileData, googlePlayUrl: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://play.google.com/store/apps/..."
                      />
                    </div>}

                    {showAppStoreSettings && <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        App Store URL
                      </label>
                      <input
                        type="url"
                        value={profileData.appStoreUrl}
                        onChange={(e) => setProfileData({ ...profileData, appStoreUrl: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://apps.apple.com/..."
                      />
                    </div>}
                  </div>

                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                    <div className="mb-3 flex items-center gap-3">
                      <Globe className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="text-sm font-semibold text-blue-900">Link Önizleme Testi</div>
                        <p className="text-xs text-blue-700">Hakkında sayfasını ve market linklerini panelden hızlıca kontrol edin.</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a href="/about" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800">
                        <ExternalLink className="h-4 w-4" />
                        Hakkında Sayfasını Aç
                      </a>
                      {showGooglePlaySettings && (
                        <a href={profileData.googlePlayUrl || '#'} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${profileData.googlePlayUrl ? 'bg-green-600 text-white hover:bg-green-700' : 'cursor-not-allowed bg-gray-200 text-gray-500'}`} onClick={(e) => { if (!profileData.googlePlayUrl) e.preventDefault(); }}>
                          <ExternalLink className="h-4 w-4" />
                          Google Play Test
                        </a>
                      )}
                      {showAppStoreSettings && (
                        <a href={profileData.appStoreUrl || '#'} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${profileData.appStoreUrl ? 'bg-black text-white hover:bg-slate-800' : 'cursor-not-allowed bg-gray-200 text-gray-500'}`} onClick={(e) => { if (!profileData.appStoreUrl) e.preventDefault(); }}>
                          <ExternalLink className="h-4 w-4" />
                          App Store Test
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="mb-4 flex items-center gap-3">
                      <Palette className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Kişisel Tema</div>
                        <p className="text-xs text-gray-500">Her kullanıcı kendi temasını seçebilir. Varsayılan tema dark olarak başlar.</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-3 md:flex-row md:items-end">
                      <div className="w-full md:max-w-xs">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Tema</label>
                        <select
                          value={themePreference}
                          onChange={(e) => setThemePreference(e.target.value as ThemePreference)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="dark">Dark Tema</option>
                          <option value="light">Light Tema</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={handleSaveThemePreference}
                        disabled={savingState.theme}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {savingState.theme ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {savingState.theme ? 'Kaydediliyor...' : 'Temayı Kaydet'}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={savingState.profile}
                      className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                    >
                      {savingState.profile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {savingState.profile ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Bell className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">Bildirim Ayarları</h2>
                </div>
                <form onSubmit={handleSaveNotifications} className="space-y-6">
                  {/* Notification Channels */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Bildirim Kanalları</h3>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={notificationSettings.emailNotifications}
                          onChange={(e) => setNotificationSettings({ ...notificationSettings, emailNotifications: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <Mail className="w-5 h-5 text-gray-600" />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">E-posta Bildirimleri</span>
                          <p className="text-xs text-gray-500">Bildirimleri e-posta ile alın</p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={notificationSettings.smsNotifications}
                          onChange={(e) => setNotificationSettings({ ...notificationSettings, smsNotifications: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <Smartphone className="w-5 h-5 text-gray-600" />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">SMS Bildirimleri</span>
                          <p className="text-xs text-gray-500">Kritik bildirimler için SMS alın</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Notification Types */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Bildirim Türleri</h3>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={notificationSettings.newShipment}
                          onChange={(e) => setNotificationSettings({ ...notificationSettings, newShipment: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900">Yeni sevkiyat oluşturulduğunda</span>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={notificationSettings.shipmentDelivered}
                          onChange={(e) => setNotificationSettings({ ...notificationSettings, shipmentDelivered: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900">Sevkiyat teslim edildiğinde</span>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={notificationSettings.delayAlert}
                          onChange={(e) => setNotificationSettings({ ...notificationSettings, delayAlert: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900">Gecikme uyarıları</span>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={notificationSettings.documentUploaded}
                          onChange={(e) => setNotificationSettings({ ...notificationSettings, documentUploaded: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900">Belge yüklendiğinde</span>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={notificationSettings.maintenanceReminder}
                          onChange={(e) => setNotificationSettings({ ...notificationSettings, maintenanceReminder: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900">Bakım hatırlatıcıları</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Destek Bildirimi Rolleri</h3>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {roles.map((role) => {
                        const selected = notificationSettings.supportNotificationRoleIds.includes(role.id);
                        return (
                          <label key={role.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => setNotificationSettings({
                                ...notificationSettings,
                                supportNotificationRoleIds: e.target.checked
                                  ? [...new Set([...notificationSettings.supportNotificationRoleIds, role.id])]
                                  : notificationSettings.supportNotificationRoleIds.filter((item) => item !== role.id),
                              })}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-900">{role.name}</span>
                              <p className="text-xs text-gray-500">Yeni destek mesajlarında bu role bildirim gönderilir</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <p className="mt-3 text-xs text-gray-500">Birden fazla rol seçebilirsiniz. Seçilirse aktif durumdaki tüm ilgili kullanıcılara bildirim oluşturulur.</p>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={savingState.notifications}
                      className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                    >
                      {savingState.notifications ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {savingState.notifications ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">Güvenlik Ayarları</h2>
                </div>
                <div className="space-y-6">
                  <div className="p-6 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Şifre Değiştir</h3>
                    <p className="text-sm text-gray-600 mb-4">Hesap güvenliğinizi korumak için düzenli olarak şifrenizi değiştirin</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <input
                        type="password"
                        value={securityForm.currentPassword}
                        onChange={(e) => setSecurityForm((current) => ({ ...current, currentPassword: e.target.value }))}
                        placeholder="Mevcut şifre"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                      <input
                        type="password"
                        value={securityForm.newPassword}
                        onChange={(e) => setSecurityForm((current) => ({ ...current, newPassword: e.target.value }))}
                        placeholder="Yeni şifre"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                      <input
                        type="password"
                        value={securityForm.confirmPassword}
                        onChange={(e) => setSecurityForm((current) => ({ ...current, confirmPassword: e.target.value }))}
                        placeholder="Yeni şifre tekrar"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <button onClick={handleChangePassword} disabled={savingState.security} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                      {savingState.security ? 'Değiştiriliyor...' : 'Şifre Değiştir'}
                    </button>
                  </div>

                  <div className="p-6 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">İki Faktörlü Doğrulama</h3>
                    <p className="text-sm text-gray-600 mb-4">Hesabınıza ekstra bir güvenlik katmanı ekleyin</p>
                    <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                      Etkinleştir
                    </button>
                  </div>

                  <div className="p-6 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Aktif Oturumlar</h3>
                    <p className="text-sm text-gray-600 mb-4">Hesabınıza bağlı tüm cihazları görüntüleyin</p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between p-3 bg-white rounded border border-gray-200">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Chrome - Windows</p>
                          <p className="text-xs text-gray-500">İstanbul, Türkiye • Şu anda aktif</p>
                        </div>
                        <span className="text-xs text-green-600 font-medium">Mevcut</span>
                      </div>
                    </div>
                    <button className="px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50">
                      Tüm Oturumları Sonlandır
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && (
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Plug className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">Entegrasyon Ayarları</h2>
                </div>
                <form onSubmit={handleSaveIntegrations} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tachograf Entegrasyonu
                      </label>
                      <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={integrationSettings.tachographEnabled}
                          onChange={(e) => setIntegrationSettings({ ...integrationSettings, tachographEnabled: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Tachograf Entegrasyonu</span>
                          <p className="text-xs text-gray-500">Tachograf verilerini entegre et</p>
                        </div>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tachograf API URL
                      </label>
                      <input
                        type="text"
                        value={integrationSettings.tachographApiUrl}
                        onChange={(e) => setIntegrationSettings({ ...integrationSettings, tachographApiUrl: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tachograf API Anahtarı
                      </label>
                      <input
                        type="text"
                        value={integrationSettings.tachographApiKey}
                        onChange={(e) => setIntegrationSettings({ ...integrationSettings, tachographApiKey: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tachograf Zaman Aşımı (Saniye)
                      </label>
                      <input
                        type="number"
                        value={integrationSettings.tachographTimeout}
                        onChange={(e) => setIntegrationSettings({ ...integrationSettings, tachographTimeout: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={integrationSettings.tachographAutoSync}
                          onChange={(e) => setIntegrationSettings({ ...integrationSettings, tachographAutoSync: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Otomatik Senkronizasyon</span>
                          <p className="text-xs text-gray-500">Tachograf verilerini otomatik senkronize et</p>
                        </div>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Senkronizasyon Aralığı (Dakika)
                      </label>
                      <input
                        type="number"
                        value={integrationSettings.tachographSyncInterval}
                        onChange={(e) => setIntegrationSettings({ ...integrationSettings, tachographSyncInterval: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Araç Takip Entegrasyonu
                      </label>
                      <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={integrationSettings.vehicleTrackingEnabled}
                          onChange={(e) => setIntegrationSettings({ ...integrationSettings, vehicleTrackingEnabled: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Araç Takip Entegrasyonu</span>
                          <p className="text-xs text-gray-500">Araç takip verilerini entegre et</p>
                        </div>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Araç Takip API URL
                      </label>
                      <input
                        type="text"
                        value={integrationSettings.vehicleTrackingApiUrl}
                        onChange={(e) => setIntegrationSettings({ ...integrationSettings, vehicleTrackingApiUrl: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Araç Takip API Anahtarı
                      </label>
                      <input
                        type="text"
                        value={integrationSettings.vehicleTrackingApiKey}
                        onChange={(e) => setIntegrationSettings({ ...integrationSettings, vehicleTrackingApiKey: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Araç Takip Zaman Aşımı (Saniye)
                      </label>
                      <input
                        type="number"
                        value={integrationSettings.vehicleTrackingTimeout}
                        onChange={(e) => setIntegrationSettings({ ...integrationSettings, vehicleTrackingTimeout: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={integrationSettings.vehicleTrackingAutoSync}
                          onChange={(e) => setIntegrationSettings({ ...integrationSettings, vehicleTrackingAutoSync: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Otomatik Senkronizasyon</span>
                          <p className="text-xs text-gray-500">Araç takip verilerini otomatik senkronize et</p>
                        </div>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Senkronizasyon Aralığı (Dakika)
                      </label>
                      <input
                        type="number"
                        value={integrationSettings.vehicleTrackingSyncInterval}
                        onChange={(e) => setIntegrationSettings({ ...integrationSettings, vehicleTrackingSyncInterval: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Araç Takip Sağlayıcısı
                      </label>
                      <select
                        value={integrationSettings.vehicleTrackingProvider}
                        onChange={(e) => setIntegrationSettings({ ...integrationSettings, vehicleTrackingProvider: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="generic">Genel</option>
                        <option value="provider1">Sağlayıcı 1</option>
                        <option value="provider2">Sağlayıcı 2</option>
                      </select>
                    </div>

                    <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">Stokbar Müşteri Delta Senkronizasyonu</h3>
                          <p className="mt-1 text-xs text-slate-500">Aktifse sistem her 5 dakikada bir son kontrol zamanına göre `customersenkron` sorgusu atar, müşteri kayıtlarını günceller veya yeni kayıt ekler.</p>
                        </div>
                        <label className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 cursor-pointer hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={integrationSettings.stokbarCustomerSyncEnabled}
                            onChange={(e) => setIntegrationSettings({ ...integrationSettings, stokbarCustomerSyncEnabled: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <div className="text-sm font-medium text-slate-900">Aktif</div>
                            <div className="text-xs text-slate-500">5 dakikada bir otomatik calissin</div>
                          </div>
                        </label>
                      </div>

                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Son Stokbar Senkronizasyonu
                          </label>
                          <input
                            type="text"
                            value={integrationSettings.stokbarCustomerLastSyncAt || ''}
                            readOnly
                            placeholder="Henüz çalışmadı"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-slate-700"
                          />
                        </div>
                        <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-xs text-blue-800">
                          Sorgu formatı: <span className="font-mono">https://yukortagim.doguscay.com.tr/stokbar.php?type=customersenkron&date=2021-05-14 13:00</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={savingState.integrations}
                      className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                    >
                      {savingState.integrations ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {savingState.integrations ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Optimization Tab */}
            {activeTab === 'optimization' && (
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">Optimizasyon Ayarları</h2>
                </div>
                <form onSubmit={handleSaveOptimization} className="space-y-6">
                  <div className="space-y-6">
                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between gap-4 mb-4">
                        <div>
                          <h3 className="text-base font-semibold text-slate-900">Özmal KM Maliyet Tablosu</h3>
                          <p className="text-xs text-slate-500 mt-1">Dönem bilgileri ve maliyet kalemleri ile özmal araç TL/KM maliyeti otomatik hesaplanır.</p>
                        </div>
                        <div className="rounded-lg bg-emerald-50 px-4 py-3 text-right">
                          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Maliyet TL/KM</div>
                          <div className="text-2xl font-bold text-emerald-700">₺ {toFixed2(computedOzmalKmCost)}</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Dönem</label>
                          <input value={optimizationSettings.ozmalCostPeriod} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalCostPeriod: e.target.value })} placeholder="Örn: 2026/Q2" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Dönem Başı</label>
                          <input type="date" value={optimizationSettings.ozmalCostPeriodStart} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalCostPeriodStart: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Dönem Sonu</label>
                          <input type="date" value={optimizationSettings.ozmalCostPeriodEnd} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalCostPeriodEnd: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Yıllık / Dönem Km</label>
                          <input type="number" min="1" value={optimizationSettings.ozmalAnnualKm} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalAnnualKm: e.target.value })} className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`} />
                        </div>
                      </div>

                      <div className="overflow-x-auto rounded-xl border border-slate-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">S.N.</th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-700">Sabitler</th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-700">Yıllık</th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-700">TL/KM</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-t"><td className="px-3 py-2"></td><td className="px-3 py-2 font-medium text-slate-800">Trafik Sigortası</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.ozmalTrafficInsurance} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalTrafficInsurance: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-right">₺ {toFixed4(annualKm > 0 ? toNumber(optimizationSettings.ozmalTrafficInsurance) / annualKm : 0)}</td></tr>
                            <tr className="border-t"><td className="px-3 py-2"></td><td className="px-3 py-2 font-medium text-slate-800">Kasko (Çekici + Dorse)</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.ozmalCasco} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalCasco: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-right">₺ {toFixed4(annualKm > 0 ? toNumber(optimizationSettings.ozmalCasco) / annualKm : 0)}</td></tr>
                            <tr className="border-t"><td className="px-3 py-2"></td><td className="px-3 py-2 font-medium text-slate-800">MTV</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.ozmalMtv} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalMtv: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-right">₺ {toFixed4(annualKm > 0 ? toNumber(optimizationSettings.ozmalMtv) / annualKm : 0)}</td></tr>
                            <tr className="border-t"><td className="px-3 py-2"></td><td className="px-3 py-2 font-medium text-slate-800">Araç Muayene</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.ozmalInspection} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalInspection: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-right">₺ {toFixed4(annualKm > 0 ? toNumber(optimizationSettings.ozmalInspection) / annualKm : 0)}</td></tr>
                            <tr className="border-t bg-slate-50 font-semibold"><td className="px-3 py-2">1.</td><td className="px-3 py-2">Sabitler Toplamı</td><td className="px-3 py-2 text-right">₺ {toFixed2(fixedAnnualTotal)}</td><td className="px-3 py-2 text-right">₺ {toFixed4(fixedKmCost)}</td></tr>

                            <tr className="border-t"><td className="px-3 py-2"></td><td className="px-3 py-2 font-medium text-slate-800">Yakıt Miktarı (Litre)</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.ozmalFuelLiters} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalFuelLiters: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-right">-</td></tr>
                            <tr className="border-t"><td className="px-3 py-2"></td><td className="px-3 py-2 font-medium text-slate-800">Yakıt Birim Bedeli</td><td className="px-3 py-2"><input type="number" step="0.01" value={optimizationSettings.ozmalFuelUnitPrice} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalFuelUnitPrice: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-right">-</td></tr>
                            <tr className="border-t bg-slate-50 font-semibold"><td className="px-3 py-2">2.</td><td className="px-3 py-2">Toplam Yakıt Maliyeti</td><td className="px-3 py-2 text-right">₺ {toFixed2(fuelAnnualTotal)}</td><td className="px-3 py-2 text-right">₺ {toFixed4(fuelKmCost)}</td></tr>

                            <tr className="border-t"><td className="px-3 py-2"></td><td className="px-3 py-2 font-medium text-slate-800">Lastik Adeti</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.ozmalTireCount} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalTireCount: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-right">-</td></tr>
                            <tr className="border-t"><td className="px-3 py-2"></td><td className="px-3 py-2 font-medium text-slate-800">Lastik Birim Bedeli</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.ozmalTireUnitPrice} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalTireUnitPrice: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-right">-</td></tr>
                            <tr className="border-t bg-slate-50 font-semibold"><td className="px-3 py-2">3.</td><td className="px-3 py-2">Toplam Lastik Maliyeti</td><td className="px-3 py-2 text-right">₺ {toFixed2(tireAnnualTotal)}</td><td className="px-3 py-2 text-right">₺ {toFixed4(tireKmCost)}</td></tr>

                            <tr className="border-t"><td className="px-3 py-2"></td><td className="px-3 py-2 font-medium text-slate-800">Sürücü Kişi Sayısı</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.ozmalDriverCount} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalDriverCount: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-right">-</td></tr>
                            <tr className="border-t"><td className="px-3 py-2"></td><td className="px-3 py-2 font-medium text-slate-800">Sürücü Birim Brüt Bedeli</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.ozmalDriverGross} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalDriverGross: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-right">-</td></tr>
                            <tr className="border-t bg-slate-50 font-semibold"><td className="px-3 py-2">4.</td><td className="px-3 py-2">Toplam Şoför Maliyeti</td><td className="px-3 py-2 text-right">₺ {toFixed2(driverAnnualTotal)}</td><td className="px-3 py-2 text-right">₺ {toFixed4(driverKmCost)}</td></tr>

                            <tr className="border-t"><td className="px-3 py-2">5.</td><td className="px-3 py-2 font-medium text-slate-800">Bakım</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.ozmalMaintenanceTotal} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, ozmalMaintenanceTotal: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-right">₺ {toFixed4(maintenanceKmCost)}</td></tr>

                            <tr className="border-t bg-slate-100 font-bold text-slate-900"><td className="px-3 py-2">6.</td><td className="px-3 py-2">Toplam Maliyet</td><td className="px-3 py-2 text-right">₺ {toFixed2(totalAnnualCost)}</td><td className="px-3 py-2 text-right">₺ {toFixed4(computedOzmalKmCost)}</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div>
                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="mb-4">
                          <h3 className="text-base font-semibold text-slate-900">Karar Destek Sistemi Parametreleri</h3>
                          <p className="mt-1 text-xs text-slate-500">Toplu sipariş aktarımı ve karar destek özmal maliyet hesabında öncelikli kullanılan parametreler.</p>
                        </div>
                        <div className="overflow-x-auto rounded-xl border border-slate-200">
                          <table className="min-w-full text-sm">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-semibold text-slate-700">Parametre</th>
                                <th className="px-3 py-2 text-right font-semibold text-slate-700">Değer</th>
                                <th className="px-3 py-2 text-left font-semibold text-slate-700">Açıklama</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-t"><td className="px-3 py-2">Sürücü/Personel Aylık Sabit Maliyet</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.decisionDriverMonthlyFixedCost} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, decisionDriverMonthlyFixedCost: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-slate-500">Araç başına aylık sabit gider</td></tr>
                              <tr className="border-t bg-slate-50"><td className="px-3 py-2">Günlük Personel Maliyeti</td><td className="px-3 py-2 text-right font-semibold">{toFixed2(computedDecisionDailyPersonnelCost)}</td><td className="px-3 py-2 text-slate-500">Aylık maliyet / ay toplam gün sayısı</td></tr>
                              <tr className="border-t"><td className="px-3 py-2">Mazot km Maliyeti (TL/km)</td><td className="px-3 py-2"><input type="number" step="0.01" value={optimizationSettings.decisionFuelKmCost} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, decisionFuelKmCost: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-slate-500">Araç km başına yakıt tüketim maliyeti</td></tr>
                              <tr className="border-t"><td className="px-3 py-2">Mevcut Analiz Gün Sayısı</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.decisionAnalysisDayCount} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, decisionAnalysisDayCount: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-slate-500">Analiz veri süresi</td></tr>
                              <tr className="border-t"><td className="px-3 py-2">Ay Toplam Gün Sayısı</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.decisionMonthDayCount} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, decisionMonthDayCount: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-slate-500">İlgili ayın toplam günü</td></tr>
                              <tr className="border-t bg-slate-50"><td className="px-3 py-2">Projeksiyon Katsayısı</td><td className="px-3 py-2 text-right font-semibold">{toFixed4(computedDecisionProjectionFactor)}</td><td className="px-3 py-2 text-slate-500">Ay toplam gün / analiz gün sayısı</td></tr>
                              <tr className="border-t"><td className="px-3 py-2">Backhaul (Dönüş Yükü) Yakıt Katsayısı</td><td className="px-3 py-2"><input type="number" step="0.01" value={optimizationSettings.decisionBackhaulFuelFactor} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, decisionBackhaulFuelFactor: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-slate-500">Dönüş yükü senaryoları için katsayı</td></tr>
                              <tr className="border-t"><td className="px-3 py-2">Maksimum Üçgen Rotasyon Boş KM Sınırı</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.decisionMaxTriangleEmptyKm} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, decisionMaxTriangleEmptyKm: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-slate-500">En yakın özmal için boş km üst sınırı</td></tr>
                              <tr className="border-t"><td className="px-3 py-2">Osmangazi Köprüsü Geçiş Ücreti (TL)</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.decisionOsmangaziBridgeCost} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, decisionOsmangaziBridgeCost: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-slate-500">5. sınıf TIR tek yön geçiş</td></tr>
                              <tr className="border-t"><td className="px-3 py-2">Yavuz Sultan Selim Köprüsü Geçiş Ücreti (TL)</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.decisionYssBridgeCost} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, decisionYssBridgeCost: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-slate-500">5. sınıf TIR tek yön geçiş</td></tr>
                              <tr className="border-t"><td className="px-3 py-2">Kısa Mesafe Primi</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.decisionShortDistancePrime} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, decisionShortDistancePrime: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-slate-500">Kısa rotalarda özmal prim kalemi</td></tr>
                              <tr className="border-t"><td className="px-3 py-2">Bekleme Bedeli (&lt;50 km)</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.decisionWaitingCost50Km} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, decisionWaitingCost50Km: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-slate-500">Kısa mesafe bekleme maliyeti</td></tr>
                              <tr className="border-t"><td className="px-3 py-2">Yatış Bedeli</td><td className="px-3 py-2"><input type="number" value={optimizationSettings.decisionNightCost} onChange={(e) => setOptimizationSettings({ ...optimizationSettings, decisionNightCost: e.target.value })} className={`w-full rounded border border-gray-300 px-2 py-1 text-right ${NO_SPINNER}`} /></td><td className="px-3 py-2 text-slate-500">Geceleme başına maliyet</td></tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Spot Araç KM Maliyeti (TL/km)
                      </label>
                      <div className="flex gap-2">
                         <input
                           type="number"
                           step="0.01"
                           value={optimizationSettings.spotKmCost}
                           onChange={(e) => setOptimizationSettings({ ...optimizationSettings, spotKmCost: e.target.value })}
                           className={`flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                         />
                        <button
                          type="button"
                          onClick={calculateSpotCostFromHistory}
                          disabled={calculatingSpotCost}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {calculatingSpotCost ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                          Hesapla
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Geçmiş spot tedariklerin ortalama km maliyeti</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ortalama Araç Hızı (km/saat)
                      </label>
                       <input
                         type="number"
                         value={optimizationSettings.averageSpeedKmH}
                         onChange={(e) => setOptimizationSettings({ ...optimizationSettings, averageSpeedKmH: e.target.value })}
                         className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                       />
                      <p className="text-xs text-gray-500 mt-1">Yolculuk süresi hesaplamak için kullanılacak ortalama hız</p>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={savingState.optimization}
                      className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {savingState.optimization ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Kaydet
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'static-definitions' && (
              <div className="p-6">
                <div className="mb-6 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Statik Tanımlar</h2>
                    <p className="mt-1 text-sm text-gray-500">Uygulamadaki statik liste alanlarını tablo bazlı yönetin.</p>
                  </div>
                  <button type="button" onClick={() => openStaticDefinitionModal()} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
                    <Plus className="h-4 w-4" /> Yeni Tanım
                  </button>
                </div>

                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Statik Tanım</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Tanım Türü</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Açıklama</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Değer Sayısı</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-500">İşlem</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {staticDefinitions.map((definition) => (
                        <tr key={definition.id}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{definition.name}</div>
                            <div className="mt-1 text-xs text-gray-500">{definition.key}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-700">{definition.inputType}</td>
                          <td className="px-4 py-3 text-gray-700">{definition.description || '-'}</td>
                          <td className="px-4 py-3 text-gray-700">{Array.isArray(definition.items) ? definition.items.length : 0}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button type="button" onClick={() => openItemManager(definition)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Değerler</button>
                              <button type="button" onClick={() => openStaticDefinitionModal(definition)} className="rounded-lg border border-blue-200 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"><Edit className="mr-1 inline h-4 w-4" /> Düzenle</button>
                              <button type="button" onClick={() => void handleDeleteStaticDefinition(definition)} className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"><Trash2 className="mr-1 inline h-4 w-4" /> Sil</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {staticDefinitions.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-4 py-10 text-center text-gray-500">Statik tanım bulunamadı.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Procedure Steps Tab */}
            {activeTab === 'procedure-steps' && (
              <div className="p-6">
                <div className="mb-6 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Prosedür Adımları</h2>
                    <p className="mt-1 text-sm text-gray-500">Özmal sürücü süreç takibi adımlarını yönetin. Sıralama, başlık, açıklama ve görsel düzenleyebilirsiniz.</p>
                  </div>
                  <button type="button" onClick={() => openProcedureStepModal()} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
                    <Plus className="h-4 w-4" /> Yeni Adım
                  </button>
                </div>

                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-500 w-16">Sıra</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Başlık</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Açıklama</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Görsel</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-500">Durum</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-500">İşlem</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {procedureSteps.map((step) => (
                        <tr key={step.id}>
                          <td className="px-4 py-3 text-gray-700">{step.step_order}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{step.title}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-700 max-w-xs truncate">{step.description || '-'}</td>
                          <td className="px-4 py-3">
                            {step.image_url ? (
                              <img src={step.image_url} alt={step.title} className="h-12 w-20 rounded object-cover border" />
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${step.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                              {step.is_active ? 'Aktif' : 'Pasif'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button type="button" onClick={() => openProcedureStepModal(step)} className="rounded-lg border border-blue-200 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50">
                                <Edit className="mr-1 inline h-4 w-4" /> Düzenle
                              </button>
                              <button type="button" onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = 'image/*';
                                input.onchange = (e: any) => { if (e.target?.files?.[0]) handleUploadProcedureImage(step.id, e.target.files[0]); };
                                input.click();
                              }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                <ImageUp className="mr-1 inline h-4 w-4" /> Görsel
                              </button>
                              <button type="button" onClick={() => void handleDeleteProcedureStep(step)} className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50">
                                <Trash2 className="mr-1 inline h-4 w-4" /> Sil
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {procedureSteps.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-10 text-center text-gray-500">Henüz adım eklenmemiş.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Procedure Step Modal */}
                {procedureStepModalOpen && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
                      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {editingProcedureStep ? 'Adım Düzenle' : 'Yeni Adım'}
                        </h3>
                        <button type="button" onClick={() => { setProcedureStepModalOpen(false); setEditingProcedureStep(null); }} className="text-gray-400 hover:text-gray-600">
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                      <form onSubmit={handleSaveProcedureStep} className="space-y-4 p-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
                            <input type="number" value={procedureStepForm.step_order} onChange={(e) => setProcedureStepForm((prev) => ({ ...prev, step_order: Number(e.target.value) }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                            <input type="text" value={procedureStepForm.category} onChange={(e) => setProcedureStepForm((prev) => ({ ...prev, category: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Başlık <span className="text-red-500">*</span></label>
                          <input type="text" value={procedureStepForm.title} onChange={(e) => setProcedureStepForm((prev) => ({ ...prev, title: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                          <textarea value={procedureStepForm.description} onChange={(e) => setProcedureStepForm((prev) => ({ ...prev, description: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" rows={4} />
                        </div>
                        {procedureStepForm.image_url && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Mevcut Görsel</label>
                            <img src={procedureStepForm.image_url} alt="preview" className="h-24 rounded border object-cover" />
                          </div>
                        )}
                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                          <button type="button" onClick={() => { setProcedureStepModalOpen(false); setEditingProcedureStep(null); }} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">İptal</button>
                          <button type="submit" disabled={savingState.procedureSteps} className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
                            {savingState.procedureSteps ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {editingProcedureStep ? 'Güncelle' : 'Oluştur'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Harcirah Tab */}
            {activeTab === 'harcirah' && (
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">Harcırah Ayarları</h2>
                </div>
                <form onSubmit={handleSaveHarcirah} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {([
                      { key: 'prim', label: 'PRİM (₺/km)', desc: 'Kilometre başına prim' },
                      { key: 'maas', label: 'MAAŞ (₺)', desc: 'Aylık sabit maaş' },
                      { key: 'kisaMesafePrim', label: 'KISA MESAFE PRİM - 200 KM ALTI (₺)', desc: '200 km altı seferler için prim' },
                      { key: 'beklemeBedeli', label: 'BEKLEME BEDELİ - 50 KM ALTI (₺)', desc: '50 km altı seferler için bekleme bedeli' },
                      { key: 'vergi', label: 'VERGİ (%)', desc: 'Vergi oranı (yüzde)' },
                      { key: 'dorseKesme', label: 'DORSE KESME (₺)', desc: 'Dorse kesme bedeli' },
                      { key: 'araDepoSevki', label: 'ARA DEPO SEVKİ (₺)', desc: 'Ara depo sevk bedeli' },
                      { key: 'hamaliye', label: 'HAMALİYE (₺)', desc: 'Birim hamaliye bedeli' },
                      { key: 'aylikMaksimumHarcirah', label: 'AYLIK MAKSİMUM HARCIRAH ÜCRETİ (₺)', desc: 'Aylık tavan harcırah' },
                      { key: 'gunlukAgi', label: 'GÜNLÜK AGİ (₺)', desc: 'Günlük AGİ tutarı' },
                    ] as const).map(({ key, label, desc }) => (
                      <div key={key}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                        <p className="text-xs text-gray-500 mb-1">{desc}</p>
                        <input
                          type="number"
                          step="0.01"
                          value={harcirahSettings[key as keyof typeof harcirahSettings]}
                          onChange={(e) => setHarcirahSettings((prev) => ({ ...prev, [key]: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={savingState.harcirah}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {savingState.harcirah ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Kaydet
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* System Tab */}
            {activeTab === 'system' && (
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Database className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">Sistem Ayarları</h2>
                </div>
                <form onSubmit={handleSaveSystem} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Globe className="w-4 h-4 inline mr-1" />
                        Dil
                      </label>
                      <select
                        value={systemSettings.language}
                        onChange={(e) => setSystemSettings({ ...systemSettings, language: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="tr">Türkçe</option>
                        <option value="en">English</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Zaman Dilimi
                      </label>
                      <select
                        value={systemSettings.timezone}
                        onChange={(e) => setSystemSettings({ ...systemSettings, timezone: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Europe/Istanbul">İstanbul (UTC+3)</option>
                        <option value="Europe/London">Londra (UTC+0)</option>
                        <option value="America/New_York">New York (UTC-5)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tarih Formatı
                      </label>
                      <select
                        value={systemSettings.dateFormat}
                        onChange={(e) => setSystemSettings({ ...systemSettings, dateFormat: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Para Birimi
                      </label>
                      <select
                        value={systemSettings.currency}
                        onChange={(e) => setSystemSettings({ ...systemSettings, currency: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="TRY">TRY (₺)</option>
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={systemSettings.autoBackup}
                          onChange={(e) => setSystemSettings({ ...systemSettings, autoBackup: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Otomatik Yedekleme</span>
                          <p className="text-xs text-gray-500">Verilerinizi günlük olarak otomatik yedekle</p>
                        </div>
                      </label>
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={Boolean(systemSettings.twoFactorAuthEnabled)}
                          onChange={(e) => setSystemSettings({ ...systemSettings, twoFactorAuthEnabled: e.target.checked })}
                          className="rounded border-blue-600 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Çift Doğrulama (SMS 2FA)</span>
                          <p className="text-xs text-gray-500">Tüm kullanıcıların girişte telefonlarına gönderilen SMS kodu ile doğrulama yapmasını zorunlu kıl. Aktifleştirildiğinde tüm açık oturumlar sonlanır.</p>
                        </div>
                      </label>
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={Boolean(systemSettings.smsRequestProtection)}
                          onChange={(e) => setSystemSettings({ ...systemSettings, smsRequestProtection: e.target.checked })}
                          className="rounded border-blue-600 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">SMS İstek Koruması</span>
                          <p className="text-xs text-gray-500">Doğrulama kodu isteklerinin yalnızca uygulamamızdan gelmesini zorunlu kılar. Uygulama her telefon ekranı açılışında sunucudan üretilen bir koruma kodu alır ve kod isteğiyle birlikte gönderir. Aktifken sistem dışı otomatik kod istekleri engellenir.</p>
                        </div>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Veri Saklama Süresi (Gün)
                      </label>
                       <input
                         type="number"
                         value={systemSettings.dataRetention}
                         onChange={(e) => setSystemSettings({ ...systemSettings, dataRetention: e.target.value })}
                         className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                       />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Teklif Bedeli (TL)
                      </label>
                       <input
                         type="number"
                         min="0"
                         step="0.01"
                         value={systemSettings.minimumFreightCost}
                         onChange={(e) => setSystemSettings({ ...systemSettings, minimumFreightCost: e.target.value })}
                         className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                       />
                       <p className="text-xs text-gray-500 mt-1">
                         Suruculer bu tutarin altinda teklif gonderemez.
                       </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        İmzalı PDF E-Posta Mesajı
                      </label>
                      <textarea
                        value={systemSettings.signedPdfEmailBody}
                        onChange={(e) => setSystemSettings({ ...systemSettings, signedPdfEmailBody: e.target.value })}
                        rows={4}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Secilen sevkiyatlarin imzali on onay PDF belgeleri ekte yer almaktadir."
                      />
                     <p className="text-xs text-gray-500 mt-1">
                         Toplu imzalı PDF e-postasında gövde metni olarak kullanılır.
                       </p>
                     </div>

                     <div className="md:col-span-2">
                       <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                         <input type="checkbox" checked={Boolean(systemSettings.basePriceRestrictionEnabled)} onChange={(e) => setSystemSettings({ ...systemSettings, basePriceRestrictionEnabled: e.target.checked })} className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                         <div><span className="text-sm font-bold text-amber-900">Baz fiyat kısıtlaması aktif</span><p className="mt-1 text-xs text-amber-800">Açıkken dış nakliye dışındaki navlunlar baz fiyatı aşamaz. Navlun güncelleme yetkisi olan kullanıcılar değişiklik yapabilir.</p></div>
                       </label>
                     </div>

                     <div className="md:col-span-2">
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Konum Bildirimi Hatırlatma Mesajı
                       </label>
                       <textarea
                         value={systemSettings.locationReminderMessage}
                         onChange={(e) => setSystemSettings({ ...systemSettings, locationReminderMessage: e.target.value })}
                         rows={3}
                         className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                         placeholder="Sürücüye gönderilecek varsayılan konum hatırlatma mesajı"
                       />
                       <p className="text-xs text-gray-500 mt-1">
                         Genel Liste'deki konum hatırlatma penceresinde otomatik gelir ve gönderim öncesi değiştirilebilir.
                       </p>
                     </div>

                     <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Baz Fiyat Uzeri Teklif Limit Yuzdesi
                      </label>
                       <input
                         type="number"
                         min="0"
                         step="1"
                         value={systemSettings.maxBidOverBasePricePercent}
                         onChange={(e) => setSystemSettings({ ...systemSettings, maxBidOverBasePricePercent: e.target.value })}
                         className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                       />
                      <p className="text-xs text-gray-500 mt-1">
                        Baz fiyat varsa ayni yuzde hem alt minimum teklif siniri hem de ust uygunluk siniri icin kullanilir.
                       </p>
                     </div>

                     <div className="md:col-span-2 rounded-xl border border-orange-200 bg-orange-50 p-4">
                       <label className="flex items-center gap-3 font-semibold text-orange-900">
                         <input type="checkbox" checked={Boolean(systemSettings.driverAutoControlEnabled)} onChange={(e) => setSystemSettings({ ...systemSettings, driverAutoControlEnabled: e.target.checked })} className="h-4 w-4 rounded border-orange-300 text-orange-600" />
                         Sürücü Oto Kontrol aktif
                       </label>
                       <p className="mt-1 text-xs text-orange-800">Seyir verilerinden hız ve rölanti ihlallerini otomatik kontrol eder, aynı ihlali aynı gün tekrar göndermez.</p>
                        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                         <label className="text-sm text-orange-900">Hız limiti (km/sa)<input type="number" min="1" value={systemSettings.driverAutoControlSpeedLimit} onChange={(e) => setSystemSettings({ ...systemSettings, driverAutoControlSpeedLimit: e.target.value })} className={`mt-1 w-full rounded-lg border border-orange-200 bg-white px-3 py-2 ${NO_SPINNER}`} /></label>
                         <label className="text-sm text-orange-900">Rölanti limiti (dakika)<input type="number" min="1" value={systemSettings.driverAutoControlIdleMinutes} onChange={(e) => setSystemSettings({ ...systemSettings, driverAutoControlIdleMinutes: e.target.value })} className={`mt-1 w-full rounded-lg border border-orange-200 bg-white px-3 py-2 ${NO_SPINNER}`} /></label>
                         <label className="flex items-center gap-2 text-sm text-orange-900"><input type="checkbox" checked={Boolean(systemSettings.driverAutoControlPush)} onChange={(e) => setSystemSettings({ ...systemSettings, driverAutoControlPush: e.target.checked })} /> Uygulama bildirimi</label>
                          <label className="flex items-center gap-2 text-sm text-orange-900"><input type="checkbox" checked={Boolean(systemSettings.driverAutoControlWhatsapp)} onChange={(e) => setSystemSettings({ ...systemSettings, driverAutoControlWhatsapp: e.target.checked })} /> WhatsApp Web bildirimi</label>
                        </div>
                        <div className="mt-3 rounded-lg border border-orange-200 bg-white p-3">
                          <label className="flex items-center gap-2 text-sm font-medium text-orange-900"><input type="checkbox" checked={Boolean(systemSettings.missingDriverCodeViolationEnabled)} onChange={(e) => setSystemSettings({ ...systemSettings, missingDriverCodeViolationEnabled: e.target.checked })} /> Kartsız sürüş ihlali aktif</label>
                          <p className="mt-1 text-xs text-orange-800">Seyir canlı verisinde sürücü kodu boş, araç hareket halinde ve depo izin alanı dışındaysa kontrol edilir.</p>
                          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                            <label className="text-sm text-orange-900">Depo ihlal muafiyet yarıçapı (km)<input type="number" min="0.1" step="0.1" value={systemSettings.missingDriverCodeWarehouseRadiusKm} onChange={(e) => setSystemSettings({ ...systemSettings, missingDriverCodeWarehouseRadiusKm: e.target.value })} className={`mt-1 w-full rounded-lg border border-orange-200 bg-white px-3 py-2 ${NO_SPINNER}`} /><span className="mt-1 block text-xs">Depo merkezinden bu mesafe içinde kartsız sürüş, sürücü kartı ve takograf ihlalleri oluşturulmaz. Varsayılan: 2 km.</span></label>
                            <label className="text-sm text-orange-900">Minimum hareket mesafesi (km)<input type="number" min="0.1" step="0.1" value={systemSettings.missingDriverCodeMovementKm} onChange={(e) => setSystemSettings({ ...systemSettings, missingDriverCodeMovementKm: e.target.value })} className={`mt-1 w-full rounded-lg border border-orange-200 bg-white px-3 py-2 ${NO_SPINNER}`} /></label>
                          </div>
                        </div>
                        <textarea value={systemSettings.driverAutoControlMessage} onChange={(e) => setSystemSettings({ ...systemSettings, driverAutoControlMessage: e.target.value })} rows={3} className="mt-3 w-full rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm" />
                       <p className="mt-1 text-xs text-orange-800">Şablon alanları: [Sürücü Adı], [Plaka], [İhlal].</p>
                     </div>

                     <div className="md:col-span-2 rounded-xl border border-red-200 bg-red-50 p-4">
                       <div className="font-semibold text-red-900">Ağır İhlal Bildirimleri</div>
                       <label className="flex items-center gap-3 mt-3 text-sm text-red-900">
                         <input type="checkbox" checked={systemSettings.driverViolationDefenseRequestsEnabled !== false} onChange={(e) => setSystemSettings({ ...systemSettings, driverViolationDefenseRequestsEnabled: e.target.checked })} />
                         Savunma talebi oluşturma aktif
                       </label>
                       <p className="mt-1 text-xs text-red-800">Pasifken yeni ihlaller için savunma talebi oluşturulmaz. Mevcut talepler korunur; pasif dönemdeki ihlaller sonradan otomatik talebe dönüştürülmez.</p>
                       <label className="flex items-center gap-3 mt-3 text-sm text-red-900">
                         <input type="checkbox" checked={systemSettings.driverViolationDefenseNotificationsEnabled !== false} onChange={(e) => setSystemSettings({ ...systemSettings, driverViolationDefenseNotificationsEnabled: e.target.checked })} />
                         Savunma ve ağır ihlal bildirimleri aktif
                       </label>
                       <p className="mt-1 text-xs text-red-800">Otomatik uygulama/push bildirimleri, hatırlatmalar, otomatik SMS ve İK e-postalarını kontrol eder. Sürücünün savunma gönderirken istediği doğrulama kodu çalışmaya devam eder.</p>
                       <p className="mt-1 text-xs text-red-800">Ağır ihlal tespit edildiğinde özmal sürücüye SMS ile savunma doğrulama kodu gönderilir; İK alıcılarına aynı ihlal e-posta ile bildirilir.</p>
                       <label className="flex items-center gap-3 mt-3 text-sm text-red-900">
                         <input type="checkbox" checked={Boolean(systemSettings.driverViolationDefenseOtpEnabled)} onChange={(e) => setSystemSettings({ ...systemSettings, driverViolationDefenseOtpEnabled: e.target.checked })} className="h-4 w-4 rounded border-red-300 text-red-600" />
                         Savunma SMS doğrulama kodu aktif
                       </label>
                       <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                         <label className="block text-sm text-red-900">
                           İK e-posta alıcıları (To)
                           <textarea value={systemSettings.driverViolationHrTo} onChange={(e) => setSystemSettings({ ...systemSettings, driverViolationHrTo: e.target.value })} rows={3} className="mt-1 w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm" placeholder="ik@ornek.com, insankaynaklari@ornek.com" />
                         </label>
                         <label className="block text-sm text-red-900">
                           İK e-posta bilgi (Cc)
                           <textarea value={systemSettings.driverViolationHrCc} onChange={(e) => setSystemSettings({ ...systemSettings, driverViolationHrCc: e.target.value })} rows={3} className="mt-1 w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm" placeholder="yonetim@ornek.com" />
                         </label>
                       </div>
                        <p className="mt-1 text-xs text-red-700">Virgül, noktalı virgül veya satır sonuyla ayırarak birden fazla adres tanımlayabilirsiniz.</p>
                      </div>

                      <div className="md:col-span-2 rounded-xl border border-orange-200 bg-orange-50 p-4">
                        <div className="font-semibold text-orange-900">Günlük İhlal Özeti Maili</div>
                        <p className="mt-1 text-xs text-orange-800">
                          Belirlenen saatte bir önceki dönemin ihlal özeti (KPI, sürücü × ihlal türü tablosu, tür toplamları ve ağır ihlal detayı) e-posta ile gönderilir. Saat 12:00'den önce çalışırsa önceki gün 12:00 - bugün, sonrasında ise bugün 00:00 - saat anı aralığı özetlenir.
                        </p>
                        <label className="flex items-center gap-3 mt-3 text-sm text-orange-900">
                          <input type="checkbox" checked={Boolean(systemSettings.driverViolationSummaryEnabled)} onChange={(e) => setSystemSettings({ ...systemSettings, driverViolationSummaryEnabled: e.target.checked })} className="h-4 w-4 rounded border-orange-300 text-orange-600" />
                          Günlük özet maili aktif
                        </label>
                        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                          <label className="block text-sm text-orange-900">
                            Gönderim saati
                            <input type="time" value={systemSettings.driverViolationSummaryTime} onChange={(e) => setSystemSettings({ ...systemSettings, driverViolationSummaryTime: e.target.value })} className="mt-1 w-full rounded-lg border border-orange-200 bg-white px-3 py-2" />
                          </label>
                          <label className="block text-sm text-orange-900">
                            Özet e-posta alıcıları (To)
                            <textarea value={systemSettings.driverViolationSummaryTo} onChange={(e) => setSystemSettings({ ...systemSettings, driverViolationSummaryTo: e.target.value })} rows={3} className="mt-1 w-full rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm" placeholder="ik@doguscay.com.tr" />
                          </label>
                          <label className="block text-sm text-orange-900">
                            Özet e-posta bilgi (Cc)
                            <textarea value={systemSettings.driverViolationSummaryCc} onChange={(e) => setSystemSettings({ ...systemSettings, driverViolationSummaryCc: e.target.value })} rows={3} className="mt-1 w-full rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm" placeholder="insankaynaklari@doguscay.com.tr" />
                          </label>
                        </div>
                        <p className="mt-1 text-xs text-orange-700">Alıcı boş bırakılırsa yukarıdaki İK e-posta alıcıları kullanılır.</p>
                      </div>

                     <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sabit İrtibat No
                      </label>
                      <input
                        type="text"
                        value={systemSettings.defaultShipmentContactPhone}
                        onChange={(e) => setSystemSettings({ ...systemSettings, defaultShipmentContactPhone: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Örn: 0532 123 45 67"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Sevkiyat irtibat numarası girilmezse bu sabit numara kullanılır.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Arşiv Evrak Sıra No Sayaç Başlangıcı
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={systemSettings.archiveNextFolderNumber}
                        onChange={(e) => setSystemSettings({ ...systemSettings, archiveNextFolderNumber: e.target.value.replace(/\D/g, '') || '1' })}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Fiziksel arşive her yeni kayıt gönderildiğinde bu değer 1 artar ve sıradaki evrak sıra numarası olarak kullanılır.
                      </p>
                    </div>

                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Ek Durak Bedeli
                       </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={systemSettings.extraStopFee}
                          onChange={(e) => setSystemSettings({ ...systemSettings, extraStopFee: e.target.value })}
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                          placeholder="Örn: 1000"
                        />
                       <p className="text-xs text-gray-500 mt-1">
                         Hedef fiyat hesaplamasında her ilave teslim noktası için kullanılır.
                       </p>
                      </div>

                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                        <div className="font-semibold text-emerald-900">Toplu SMS Limiti</div>
                        <label className="mt-3 flex items-center gap-2 text-sm font-medium text-emerald-900"><input type="checkbox" checked={Boolean(systemSettings.smsNotificationsEnabled)} onChange={(e) => setSystemSettings({ ...systemSettings, smsNotificationsEnabled: e.target.checked })} /> SMS bildirimleri aktif</label>
                        <label className="mt-3 block text-sm text-emerald-900">
                          Gunluk maksimum SMS adedi
                          <input type="number" min="1" step="1" value={systemSettings.bulkSmsDailyLimit} onChange={(e) => setSystemSettings({ ...systemSettings, bulkSmsDailyLimit: e.target.value })} className={`mt-1 w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 ${NO_SPINNER}`} />
                        </label>
                        <p className="mt-1 text-xs text-emerald-800">Kapalıyken toplu SMS dahil tüm bildirim SMS'leri gönderilmez. Doğrulama kodu SMS'leri bu ayardan etkilenmez. Tüm SMS kanalları ortak günlük kotaya dahildir. Varsayılan: 2000. Her SMS metni en fazla 140 karakter olabilir.</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Müşteri - Depo Baz Fiyat İndirimi (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={systemSettings.customerToDepotBasePriceDiscountPercent}
                          onChange={(e) => setSystemSettings({ ...systemSettings, customerToDepotBasePriceDiscountPercent: e.target.value })}
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                          placeholder="Örn: 20"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Yükleme noktası müşteri, teslim noktası depo olduğunda ters yöndeki baz fiyat bu oran kadar indirilir. Ek durak bedeli indirime dahil edilmez.
                        </p>
                      </div>

                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Birim Durak Bedeli
                      </label>
                       <input
                         type="number"
                         min="0"
                         step="0.01"
                         value={systemSettings.unitStopFee}
                         onChange={(e) => setSystemSettings({ ...systemSettings, unitStopFee: e.target.value })}
                         className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                         placeholder="Örn: 950"
                       />
                      <p className="text-xs text-gray-500 mt-1">
                        Ara nokta bulunan sevkiyatlarda durak bedeli bu birim değer üzerinden hesaplanır.
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={systemSettings.autoCalculateStopFees !== false}
                          onChange={(e) => setSystemSettings({ ...systemSettings, autoCalculateStopFees: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Durak bedellerini otomatik hesapla</span>
                          <p className="text-xs text-gray-500">İşaretli değilse genel liste ve finansal işlemlerde tüm siparişlerin durak bedeli 0 gösterilir.</p>
                        </div>
                      </label>
                    </div>

                    <div className="md:col-span-2">
                      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="flex items-center gap-2">
                            <Timer className="w-5 h-5 text-blue-600" />
                            <h3 className="text-base font-semibold text-gray-900">Otomatik Yatma Bedeli Hesaplama</h3>
                          </div>
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={systemSettings.demurrageEnabled !== false}
                              onChange={(e) => setSystemSettings({ ...systemSettings, demurrageEnabled: e.target.checked })}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            Etkin
                          </label>
                        </div>
                        <p className="text-xs text-gray-600 mb-3">
                          Geçiş tarihinden itibaren spot sürücü görünümünde manuel bekleme bedeli girişi kapatılır ve yatma bedeli
                          bu kurallara göre otomatik hesaplanır.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Geçiş Tarihi
                            </label>
                            <input
                              type="date"
                              value={systemSettings.demurrageTransitionDate}
                              onChange={(e) => setSystemSettings({ ...systemSettings, demurrageTransitionDate: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Bu tarihten sonra yalnızca otomatik hesaplama çalışır.
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Günlük Yatma Bedeli (TL/Gün)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={systemSettings.demurrageDailyFee}
                              onChange={(e) => setSystemSettings({ ...systemSettings, demurrageDailyFee: e.target.value })}
                              className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                              placeholder="Örn: 750"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              B_günlük: Hakedilen yatma günü başına ödenecek birim tutar.
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Geofence Yarıçapı (km)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={systemSettings.demurrageGeofenceKm}
                              onChange={(e) => setSystemSettings({ ...systemSettings, demurrageGeofenceKm: e.target.value })}
                              className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                              placeholder="Örn: 2"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Araç teslim noktasına bu mesafeye kadar yaklaştığında varış zamanı kabul edilir.
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Yeniden Hesaplama Tarama Süresi (Gün)
                            </label>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={systemSettings.demurrageMaxLookaheadDays}
                              onChange={(e) => setSystemSettings({ ...systemSettings, demurrageMaxLookaheadDays: e.target.value })}
                              className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                              placeholder="Örn: 2"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Yükleme tarihinden itibaren kaç günlük konum verisi taranır.
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Ayrılış Geofence Çarpanı
                            </label>
                            <input
                              type="number"
                              min="1"
                              step="0.1"
                              value={systemSettings.demurrageExitGeofenceMultiplier}
                              onChange={(e) => setSystemSettings({ ...systemSettings, demurrageExitGeofenceMultiplier: e.target.value })}
                              className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                              placeholder="Örn: 1"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Ayrılış, geofence yarıçapı bu değerle çarpıldıktan sonra dışarı çıkınca yazılır. 1 = geofence sınırı.
                            </p>
                          </div>
                          <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Müşteri Kesim Saati
                              </label>
                              <input
                                type="time"
                                value={systemSettings.demurrageCustomerCutoffTime}
                                onChange={(e) => setSystemSettings({ ...systemSettings, demurrageCustomerCutoffTime: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Müşteri teslim noktalarında ilk gün için geçerli varış kesim saati.
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Depo Kesim Saati
                              </label>
                              <input
                                type="time"
                                value={systemSettings.demurrageDepotCutoffTime}
                                onChange={(e) => setSystemSettings({ ...systemSettings, demurrageDepotCutoffTime: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Depo teslim noktalarında ilk gün için geçerli varış kesim saati.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Spot Sürücü Zorunlu Evrakları
                      </label>
                      <div className="grid grid-cols-1 gap-2 rounded-lg border border-gray-300 bg-gray-50 p-3 md:grid-cols-2">
                        {SPOT_DRIVER_REQUIRED_DOCUMENT_OPTIONS.map((item) => {
                          const selected = Array.isArray(systemSettings.requiredSpotDriverDocuments) && systemSettings.requiredSpotDriverDocuments.includes(item.value);
                          return (
                            <label key={item.value} className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => setSystemSettings({
                                  ...systemSettings,
                                  requiredSpotDriverDocuments: e.target.checked
                                    ? [...new Set([...(systemSettings.requiredSpotDriverDocuments || []), item.value])]
                                    : (systemSettings.requiredSpotDriverDocuments || []).filter((value: string) => value !== item.value),
                                })}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span>{item.label}</span>
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Seçili evrakları yüklemeyen spot sürücüler teklif bildirimi alamaz, teklif taleplerini göremez ve teklif veremez.
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bildirim Gönderilecek Spot Sürücüler
                      </label>
                      <div className="relative" ref={spotDriverDropdownRef}>
                        {selectedSpotDrivers.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {selectedSpotDrivers.map((driver) => (
                              <span key={driver.id} className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2.5 py-1 text-xs font-medium text-violet-700">
                                {driver.full_name || driver.phone || `#${driver.id}`}
                                <button
                                  type="button"
                                  onClick={() => setSystemSettings({
                                    ...systemSettings,
                                    allowedSpotDriverIds: (systemSettings.allowedSpotDriverIds || []).filter((id: string) => id !== driver.id),
                                  })}
                                  className="ml-0.5 rounded-full p-0.5 hover:bg-violet-200 transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="relative">
                          <input
                            type="text"
                            value={spotDriverSearch}
                            onChange={(e) => { setSpotDriverSearch(e.target.value); setSpotDriverDropdownOpen(true); }}
                            onFocus={() => setSpotDriverDropdownOpen(true)}
                            placeholder={selectedSpotDrivers.length === 0 ? 'Sürücü adı veya telefon ile arayın...' : 'Yeni sürücü ekle...'}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          {spotDriverSearch && (
                            <button
                              type="button"
                              onClick={() => { setSpotDriverSearch(''); setSpotDriverDropdownOpen(false); }}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        {spotDriverDropdownOpen && filteredExternalDrivers.length > 0 && (
                          <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                            {filteredExternalDrivers.slice(0, 30).map((driver) => {
                              const isSelected = (systemSettings.allowedSpotDriverIds || []).includes(driver.id);
                              return (
                                <button
                                  key={driver.id}
                                  type="button"
                                  onClick={() => {
                                    if (isSelected) {
                                      setSystemSettings({
                                        ...systemSettings,
                                        allowedSpotDriverIds: (systemSettings.allowedSpotDriverIds || []).filter((id: string) => id !== driver.id),
                                      });
                                    } else {
                                      setSystemSettings({
                                        ...systemSettings,
                                        allowedSpotDriverIds: [...new Set([...(systemSettings.allowedSpotDriverIds || []), driver.id])],
                                      });
                                    }
                                    setSpotDriverSearch('');
                                    setSpotDriverDropdownOpen(false);
                                  }}
                                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${isSelected ? 'bg-violet-50' : ''}`}
                                >
                                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0 ${isSelected ? 'bg-violet-200 text-violet-700' : 'bg-gray-100 text-gray-500'}`}>
                                    {(driver.full_name || driver.phone || '?').charAt(0).toUpperCase('tr-TR')}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <span className="block truncate font-medium text-gray-800">{driver.full_name || `Sürücü #${driver.id}`}</span>
                                    {driver.phone && <span className="block text-xs text-gray-400">{driver.phone}</span>}
                                  </div>
                                  {isSelected && <CheckCircle2 className="h-4 w-4 text-violet-500 flex-shrink-0" />}
                                </button>
                              );
                            })}
                            {filteredExternalDrivers.length > 30 && (
                              <div className="px-3 py-2 text-xs text-gray-400 text-center">+{filteredExternalDrivers.length - 30} sonuç daha var</div>
                            )}
                          </div>
                        )}
                        {spotDriverDropdownOpen && spotDriverSearch && filteredExternalDrivers.length === 0 && (
                          <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white p-3 text-center text-sm text-gray-400 shadow-lg">
                            Eşleşen sürücü bulunamadı
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Liste boşsa tüm uygun spot sürücülere bildirim gider. Sürücü eklenirse sadece listedekilere bildirim gönderilir.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        İç Nakliye Fatura Müşterisi
                      </label>
                      <select
                        value={systemSettings.internalFreightInvoiceCustomerId}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedCustomer = customers.find((item) => item.id === selectedId);
                          setSystemSettings({
                            ...systemSettings,
                            internalFreightInvoiceCustomerId: selectedId,
                            internalFreightInvoiceCustomerName: selectedCustomer?.name || '',
                          });
                        }}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seçiniz</option>
                        {systemSettings.internalFreightInvoiceCustomerId && !customers.some((item) => item.id === systemSettings.internalFreightInvoiceCustomerId) && (
                          <option value={systemSettings.internalFreightInvoiceCustomerId}>{systemSettings.internalFreightInvoiceCustomerName || 'Mevcut Seçim'}</option>
                        )}
                        {customers.map((item) => (
                          <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Bu müşteri dışındaki fatura müşterileri ile açılan yeni sevk siparişleri dış nakliye olarak işaretlenir.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Spot Canlı Takip Dakika Limiti
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={systemSettings.spotLiveTrackingRecencyMinutes}
                        onChange={(e) => setSystemSettings({ ...systemSettings, spotLiveTrackingRecencyMinutes: e.target.value })}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                        placeholder="Örn: 120"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Spot araçlar canlı takipte sadece son bu kadar dakika içinde konum gönderdiyse gösterilir. 0 girerseniz kısıt kapatılır.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Teklif / Bildirim Km Çapı
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={systemSettings.offerNotificationRadiusKm}
                        onChange={(e) => setSystemSettings({ ...systemSettings, offerNotificationRadiusKm: e.target.value })}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                        placeholder="Örn: 50"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        0 girilirse teklif talepleri ve bildirimler tüm uygun sürücülere gider. 0'dan büyük değerlerde, yükleme noktasına bu çap içinde ve canlı konumu güncel olan sürücüler görür.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rampa Yükleme/Boşaltma Süresi (Dakika)
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={systemSettings.rampOperationDurationMinutes}
                        onChange={(e) => setSystemSettings({ ...systemSettings, rampOperationDurationMinutes: e.target.value })}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                        placeholder="Örn: 60"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Rampa gant planında her yükleme/boşaltma kutusunun varsayılan süresi bu değer üzerinden hesaplanır.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kantar Yaklaşım Çapı (Km)
                      </label>
                      <input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={systemSettings.weighbridgeProximityRadiusKm}
                        onChange={(e) => setSystemSettings({ ...systemSettings, weighbridgeProximityRadiusKm: e.target.value })}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                        placeholder="Örn: 1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Arka planda 5 dakikada bir çalışan kantar yaklaşım kontrolünde araç bu yol mesafesine girerse bildirim üretilir.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Timeline Varış Toleransı (Km)
                      </label>
                      <input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={systemSettings.timelineArrivalRadiusKm}
                        onChange={(e) => setSystemSettings({ ...systemSettings, timelineArrivalRadiusKm: e.target.value })}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                        placeholder="Örn: 0.5"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Aracın rota noktasına varmış sayılması için kullanılacak mesafe toleransı.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Timeline Ayrılış Toleransı (Km)
                      </label>
                      <input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={systemSettings.timelineDepartureRadiusKm}
                        onChange={(e) => setSystemSettings({ ...systemSettings, timelineDepartureRadiusKm: e.target.value })}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`}
                        placeholder="Örn: 0.7"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Aracın rota noktasından ayrılmış sayılması için kullanılacak mesafe toleransı.
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Globe className="w-4 h-4 inline mr-1" />
                        Google Maps API Key
                      </label>
                      <input
                        type="password"
                        value={systemSettings.googleMapsApiKey}
                        onChange={(e) => setSystemSettings({ ...systemSettings, googleMapsApiKey: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="AIzaSy..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Harita, adres otomatik tamamlama ve mesafe hesaplama için kullanılır.
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Globe className="w-4 h-4 inline mr-1" />
                        Bing Maps API Key
                      </label>
                      <input
                        type="password"
                        value={systemSettings.bingMapsApiKey}
                        onChange={(e) => setSystemSettings({ ...systemSettings, bingMapsApiKey: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="AkdB..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Google Maps başarısız olduğunda yedek mesafe hesaplama için kullanılır.
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Globe className="w-4 h-4 inline mr-1" />
                        OpenRouteService API Key
                      </label>
                      <input
                        type="password"
                        value={systemSettings.openRouteServiceApiKey}
                        onChange={(e) => setSystemSettings({ ...systemSettings, openRouteServiceApiKey: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="5b3ce3597851110001cf6248..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Alternatif kamyon rota ve mesafe hesaplama için kullanılır.
                      </p>
                    </div>

                    <div className="md:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                      <div className="mb-4 flex items-center gap-3">
                        <Mail className="h-5 w-5 text-emerald-600" />
                        <div>
                          <div className="text-sm font-semibold text-emerald-900">SMTP Ayarları</div>
                          <p className="text-xs text-emerald-700">Plesk veya harici SMTP sunucusu bilgilerini buradan girin.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host</label>
                          <input type="text" value={systemSettings.smtpHost} onChange={(e) => setSystemSettings({ ...systemSettings, smtpHost: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="smtp.ornek.com" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Port</label>
                          <input type="number" value={systemSettings.smtpPort} onChange={(e) => setSystemSettings({ ...systemSettings, smtpPort: e.target.value })} className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${NO_SPINNER}`} placeholder="587" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Kullanıcı</label>
                          <input type="text" value={systemSettings.smtpUser} onChange={(e) => setSystemSettings({ ...systemSettings, smtpUser: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="noreply@alanadiniz.com" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Gönderen E-Posta</label>
                          <input type="email" value={systemSettings.smtpFrom} onChange={(e) => setSystemSettings({ ...systemSettings, smtpFrom: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="noreply@alanadiniz.com" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Şifre</label>
                          <input type="password" value={systemSettings.smtpPass} onChange={(e) => setSystemSettings({ ...systemSettings, smtpPass: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="SMTP şifresi" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50 border border-gray-200">
                            <input type="checkbox" checked={Boolean(systemSettings.smtpSecure)} onChange={(e) => setSystemSettings({ ...systemSettings, smtpSecure: e.target.checked })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                            <div>
                              <span className="text-sm font-medium text-gray-900">Secure SMTP kullan</span>
                              <p className="text-xs text-gray-500">Genelde 465 için açık, 587 için kapalı olur.</p>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Odeme Vadesi (Gun)
                      </label>
                       <select
                         value={systemSettings.paymentTermDays}
                         onChange={(e) => setSystemSettings({ ...systemSettings, paymentTermDays: e.target.value })}
                         className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                       >
                         {!['7', '14', '30', '45', '60'].includes(String(systemSettings.paymentTermDays || '')) && (
                           <option value={systemSettings.paymentTermDays}>{systemSettings.paymentTermDays} GÜN (Mevcut)</option>
                         )}
                         <option value="7">7 GÜN</option>
                         <option value="14">14 GÜN</option>
                         <option value="30">30 GÜN</option>
                         <option value="45">45 GÜN</option>
                         <option value="60">60 GÜN</option>
                       </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Fatura onay tarihine bu gun sayisi eklenerek en gec odeme tarihi hesaplanir.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <div className="flex flex-col gap-3 md:w-full md:max-w-md md:mr-auto">
                      <label className="block text-sm font-medium text-gray-700">Test E-Posta Adresi</label>
                      <div className="flex flex-col gap-2 md:flex-row">
                        <input
                          type="email"
                          value={testEmailAddress}
                          onChange={(e) => setTestEmailAddress(e.target.value)}
                          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="test@ornek.com"
                        />
                        <button
                          type="button"
                          onClick={handleSendTestEmail}
                          disabled={savingState.testEmail}
                          className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {savingState.testEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                          {savingState.testEmail ? 'Gonderiliyor...' : 'Test E-Postasi Gonder'}
                        </button>
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={savingState.system}
                      className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                    >
                      {savingState.system ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {savingState.system ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                  </div>
                </form>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Uygulama Versiyonu</span>
                    <span className="font-medium text-gray-700">{deployedVersionInfo?.buildId ? `Yayındaki: ${deployedVersionInfo.buildId}` : 'v1.18.0'}</span>
                  </div>
                  <div className="mt-2">
                    <a href="/version-history" className="text-sm text-blue-600 hover:text-blue-700 hover:underline">
                      Versiyon geçmişini görüntüle →
                    </a>
                  </div>
                  <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-medium text-gray-900">Sürüm Güncelleme Yönetimi</div>
                      <button
                        type="button"
                        onClick={() => void checkDeployedVersion()}
                        disabled={webVersionChecking}
                        className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        {webVersionChecking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        {webVersionChecking ? 'Kontrol Ediliyor...' : 'Yeni Sürümü Kontrol Et'}
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Yeni bir sürüm yayınlandığında tüm kullanıcıların sayfası otomatik olarak yenilenir.
                    </p>
                    {deployedVersionInfo && (
                      <div className="mt-3 space-y-1 text-xs text-gray-600">
                        <div>
                          <span className="font-medium text-gray-700">Yayındaki sürüm:</span> {deployedVersionInfo.buildId}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Yayın zamanı:</span>{' '}
                          {deployedVersionInfo.builtAt ? new Date(deployedVersionInfo.builtAt).toLocaleString('tr-TR') : '-'}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Bu tarayıcıdaki sürüm:</span> {webAppVersionService.getCurrentBuildId()}
                        </div>
                        {webAppVersionService.getCurrentBuildId() !== 'dev' && deployedVersionInfo.buildId !== webAppVersionService.getCurrentBuildId() && (
                          <div className="text-amber-600">Bu tarayıcıda güncel olmayan bir sürüm çalışıyor.</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {staticDefinitionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">{editingStaticDefinition ? 'Statik Tanım Düzenle' : 'Yeni Statik Tanım'}</h3>
              <button type="button" onClick={closeStaticDefinitionModal} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <div className="space-y-4 p-6">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Statik Tanım</label>
                <input value={staticDefinitionForm.name} onChange={(e) => setStaticDefinitionForm({ ...staticDefinitionForm, name: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Tanım Anahtarı</label>
                <input value={staticDefinitionForm.key} onChange={(e) => setStaticDefinitionForm({ ...staticDefinitionForm, key: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="orn: vehicle_inspection_body_condition" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Tanım Türü</label>
                <select value={staticDefinitionForm.inputType} onChange={(e) => setStaticDefinitionForm({ ...staticDefinitionForm, inputType: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                  <option value="select">select</option>
                  <option value="multiselect">multiselect</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Açıklama</label>
                <textarea value={staticDefinitionForm.description} onChange={(e) => setStaticDefinitionForm({ ...staticDefinitionForm, description: e.target.value })} rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
              <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button type="button" onClick={closeStaticDefinitionModal} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="button" onClick={() => void handleSaveStaticDefinition()} className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">Kaydet</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {itemManagerDefinition && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">{itemManagerDefinition.name} değerlerini yönet</h3>
              <button type="button" onClick={() => setItemManagerDefinition(null)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <div className="space-y-4 p-6">
              <div className="flex gap-3">
                <input value={newStaticItemValue} onChange={(e) => setNewStaticItemValue(e.target.value)} placeholder="Yeni değer ekle" className="flex-1 rounded-lg border border-gray-300 px-3 py-2" />
                <button type="button" onClick={() => void handleCreateStaticItem()} className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">Ekle</button>
              </div>
              {isVehicleDocumentDefinition(itemManagerDefinition) && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                  Çekici/Dorse seçimi, evrak türünün hangi belge matrisinde ve eksik evrak hesabında kullanılacağını belirler. Seçim yapılmazsa her iki matriste de gösterilir.
                </div>
              )}
              <div className="max-h-[420px] space-y-3 overflow-y-auto">
                {itemManagerRows.map((item, index) => (
                  <div key={item.id} className={`grid ${isVehicleDocumentDefinition(itemManagerDefinition) ? 'grid-cols-[minmax(0,1fr)_90px_120px_170px_auto_auto]' : 'grid-cols-[minmax(0,1fr)_90px_120px_auto_auto]'} items-center gap-3`}>
                    <input value={item.value} onChange={(e) => setItemManagerRows((current) => current.map((row) => row.id === item.id ? { ...row, value: e.target.value } : row))} className="rounded-lg border border-gray-300 px-3 py-2" />
                    <input type="number" value={item.order ?? index + 1} onChange={(e) => setItemManagerRows((current) => current.map((row) => row.id === item.id ? { ...row, order: Number(e.target.value) } : row))} className="rounded-lg border border-gray-300 px-3 py-2" />
                    <input type="number" value={item.assignmentPriority ?? 0} onChange={(e) => setItemManagerRows((current) => current.map((row) => row.id === item.id ? { ...row, assignmentPriority: Number(e.target.value) } : row))} className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Atama Önceliği" />
                    {isVehicleDocumentDefinition(itemManagerDefinition) && (
                      <div className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
                        <label className="flex items-center gap-1.5">
                          <input type="checkbox" checked={!Array.isArray(item.vehicleDocumentScope) || item.vehicleDocumentScope.length === 0 || item.vehicleDocumentScope.includes('tractor')} onChange={(event) => setVehicleDocumentScopeChecked(item.id, 'tractor', event.target.checked)} />
                          Çekici
                        </label>
                        <label className="flex items-center gap-1.5">
                          <input type="checkbox" checked={!Array.isArray(item.vehicleDocumentScope) || item.vehicleDocumentScope.length === 0 || item.vehicleDocumentScope.includes('trailer')} onChange={(event) => setVehicleDocumentScopeChecked(item.id, 'trailer', event.target.checked)} />
                          Dorse
                        </label>
                      </div>
                    )}
                    <button type="button" onClick={() => void handleUpdateStaticItem(item)} className="rounded-lg border border-blue-200 px-3 py-2 text-blue-700 hover:bg-blue-50">Kaydet</button>
                    <button type="button" onClick={() => void handleDeleteStaticItem(item)} className="rounded-lg border border-red-200 px-3 py-2 text-red-700 hover:bg-red-50">Sil</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
